import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { money } from "../../lib/api";
import { errorToastFromAxios } from "../../lib/supportToast";
import { PageHeader } from "../../components/PortalShell";
import DropshipWorkspaceShell from "../../components/DropshipWorkspaceShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { TermsEditor } from "../seller/SellerDropship";
import { LiveTrackingDialog } from "../seller/dropship/tabs";
import SearchAndFilter from "../../components/SearchAndFilter";
import DropshipReturnsPanel from "../dropship/DropshipReturnsPanel";
import CarrierClaimPill from "../dropship/CarrierClaimPill";
import CsvUploadDialog from "../dropship/CsvUploadDialog";
import CsvHistoryDrawer from "../dropship/CsvHistoryDrawer";
import SyncHandshakeCard from "../dropship/SyncHandshakeCard";
import OrderIdTriple from "../dropship/OrderIdTriple";
import FileClaimDialog, { ClaimEvidenceGallery } from "../dropship/FileClaimDialog";
import VerqoShippingClaimTimeline from "../dropship/VerqoShippingClaimTimeline";
import SyncRulesPanel from "../dropship/SyncRulesPanel";
import ChannelSetupWizard from "../dropship/ChannelSetupWizard";
import BuyerDropshipSupplierDiscovery from "../../components/BuyerDropshipSupplierDiscovery";
import BuyerDropshipEnrollmentPanel from "../../components/dropship/BuyerDropshipEnrollmentPanel";
import PendingAgreementsCallout from "../../components/dropship/PendingAgreementsCallout";
import {
  Package, Check, ArrowRight, Plus, Store, Sparkles, Zap, Settings as SettingsIcon,
  TrendingUp, RefreshCw, ExternalLink, AlertTriangle, Clock, ShieldCheck, MessageSquare,
  Users, ChevronRight, FileSignature, Settings, MapPin, Activity, Receipt, DollarSign,
  FileText, Download, Calendar, BarChart3, Plug, Inbox, Truck, CalendarClock, Upload,
  Filter, X, LayoutGrid,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// Tabs — MIRROR the seller's dropship shell 1:1. The buyer's job is to
// connect their storefront to a supplier, review/forward orders, monitor
// tracking, approve invoices, file/manage claims, and measure supplier
// performance. Same categories, buyer-appropriate labels and data.
// ─────────────────────────────────────────────────────────────────────
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";

const TABS = [
  { id: "connect",   label: "Store Setup",    icon: Plug },
  { id: "catalog",   label: "Catalog",        icon: LayoutGrid },
  { id: "orders",    label: "Daily Orders",   icon: Inbox, badge: true },
  { id: "invoices",  label: "Auto-Invoices",  icon: Receipt },
  { id: "claims",    label: "Claims",         icon: AlertTriangle },
  { id: "analytics", label: "Analytics",      icon: BarChart3 },
];

export default function BuyerDropship() {
  const [search, setSearch] = useSearchParams();
  // Legacy: old `?tab=terms` URLs land on the new Store Setup hub.
  const rawTab = search.get("tab") || "orders";
  const tab = rawTab === "terms" ? "connect" : rawTab;
  const setTab = (t) => {
    const next = new URLSearchParams(search);
    next.set("tab", t);
    setSearch(next, { replace: true });
  };

  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [movement, setMovement] = useState({ moving: 0, stalled: 0, no_movement: 0 });
  const [claims, setClaims] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [activeSupplierId, setActiveSupplierId] = useState(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderFilters, setOrderFilters] = useState({});
  const [trackOpen, setTrackOpen] = useState(null); // order for LiveTrackingDialog
  const [connectOpen, setConnectOpen] = useState(false);
  // Per-store resync state — {[storeId]: 'running' | 'done'} so we can
  // disable the button + show a transient "syncing…" state.
  const [resyncing, setResyncing] = useState({});

  // Suppliers the buyer has an *actual* dropship relationship with —
  // derived from the orders feed so platform-wide sellers don't leak in.
  // A buyer's dropship portal is 1-to-N suppliers; most buyers only ever
  // connect to one, and the UI below collapses when that's the case.
  const connectedSuppliers = useMemo(() => {
    const ids = new Set(orders.map(o => o.seller_id).filter(Boolean));
    return suppliers.filter(s => ids.has(s.id));
  }, [suppliers, orders]);

  const load = () => Promise.all([
    api.get("/dropship/stores"),
    api.get("/dropship/products"),
    api.get("/dropship/summary"),
    api.get("/dropship/orders"),
    api.get("/dropship/claims"),
    api.get("/sellers").catch(() => ({ data: [] })),
    api.post("/dropship/movement-monitor/refresh").catch(() => ({ data: {} })),
  ]).then(([s, p, sum, o, c, sup, m]) => {
    setStores(s.data); setProducts(p.data); setSummary(sum.data);
    setOrders(o.data); setClaims(c.data || []);
    setSuppliers(sup.data || []);
    setMovement(m.data || {});
    // Auto-select the first supplier that shows up in the order feed (the
    // buyer's actual connected suppliers). Falls back to first platform
    // seller so legacy flows don't break.
    const orderSellerIds = new Set((o.data || []).map(ord => ord.seller_id).filter(Boolean));
    const connected = (sup.data || []).filter(x => orderSellerIds.has(x.id));
    if (!activeSupplierId) {
      if (connected.length) setActiveSupplierId(connected[0].id);
      else if ((sup.data || []).length) setActiveSupplierId(sup.data[0].id);
    }
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!summary) return <div className="p-12 text-foreground/40">Loading…</div>;

  const seedDemoOrders = async () => {
    try {
      const { data } = await api.post("/dropship/orders/seed-demo");
      toast.success(`${data.created} mock customer orders generated · auto-forwarded to suppliers`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate demo orders");
    }
  };

  // Force-resync a single store by hitting the orchestrator's manual
  // entry point. Surfaces "X orders recovered" toast so buyers can
  // validate that a missed-webhook order shows up.
  const handleResync = async (storeId, windowHours = 24) => {
    setResyncing((s) => ({ ...s, [storeId]: true }));
    try {
      const { data } = await api.post(
        `/dropship/stores/${storeId}/resync?window_hours=${windowHours}`,
      );
      if (data.ok) {
        const newCount = data.new || 0;
        const deduped = data.deduped || 0;
        toast.success(
          newCount > 0
            ? `Synced · ${newCount} new order${newCount === 1 ? "" : "s"} recovered`
            : `Synced · 0 new orders (${deduped} already on file)`,
        );
      } else {
        toast.error(data.error || "Sync failed");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setResyncing((s) => { const next = { ...s }; delete next[storeId]; return next; });
    }
  };

  const enabled = products.filter(p => p.enabled);
  const hasStores = stores.length > 0;

  // Tab badge counts. Daily Orders surfaces tracking SLA alerts + orders
  // blocked on wallet. Claims shows pending+contested needing buyer attention.
  const counts = {
    orders:   orders.filter(o => o.status === "awaiting_payment").length
              + (movement.stalled || 0) + (movement.no_movement || 0),
    claims:   claims.filter(c => c.status === "pending" || c.status === "contested").length,
  };

  return (
    <DropshipWorkspaceShell
      role="buyer"
      pageHeader={
        <PageHeader
          title="Dropship"
          subtitle="Connect any sales channel · auto-forward customer orders · track shipments · receive daily invoices — your channel-agnostic buyer-side dropship OS"
          action={
            <div className="flex gap-2">
              <Button onClick={load} variant="outline" data-testid="dropship-refresh" className="rounded-full h-10 px-4">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Refresh
              </Button>
              <Button onClick={()=>setConnectOpen(true)} data-testid="connect-store-btn"
                className="rounded-full h-10 px-4 bg-foreground text-background hover:bg-foreground/90">
                <Plus className="w-3.5 h-3.5 mr-1.5"/>Connect channel
              </Button>
            </div>
          }
        />
      }
    >
      <div className="px-8 pb-8 pt-4">
        {/* ── Tab strip — mirrors seller exactly ──────────────────────── */}
        <div className="flex gap-1 overflow-x-auto border-b border-border -mx-8 px-8 sticky top-0 bg-background/95 backdrop-blur z-10" data-testid="dropship-tabs">
          {TABS.map(t => {
            const active = tab === t.id;
            const c = t.badge ? counts[t.id] : 0;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`drop-tab-${t.id}`}
                className={`px-3.5 py-2.5 text-sm inline-flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${active ? "border-foreground text-foreground font-medium" : "border-transparent text-foreground/55 hover:text-foreground"}`}
              >
                <t.icon className="w-3.5 h-3.5"/>{t.label}
                {c > 0 && (
                  <span className={`min-w-[20px] h-[20px] px-1.5 rounded-full text-[10.5px] font-semibold grid place-items-center ${t.id === "orders" && (movement.stalled || movement.no_movement) ? "bg-rose-100 text-rose-700" : "bg-accent text-foreground"}`}>{c > 99 ? "99+" : c}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="pt-6">
          {/* === CONNECT STORE === */}
          {tab === "connect" && (
            <div className="space-y-6">
              {/* Feb 2026 · Iter 208 · Pending signature callout for
                  buyers — surfaces drafts where the seller signed and
                  the buyer needs to counter-sign to activate. */}
              <PendingAgreementsCallout role="buyer" />
              <BuyerConnectStoreTab
                stores={stores}
                hasStores={hasStores}
                onSeedDemo={seedDemoOrders}
                onOpenConnect={()=>setConnectOpen(true)}
                onResync={handleResync}
                resyncing={resyncing}
                suppliers={connectedSuppliers}
                reload={load}
              />
              {/* Feb 2026 · Iter 207 · Per-supplier dropship enrollment
                  toggle. Toggle OFF here removes this buyer from the
                  supplier's Dropship workspace sidebar without cancelling
                  the underlying agreement (audit trail preserved). */}
              <BuyerDropshipEnrollmentPanel />
            </div>
          )}

          {/* === CATALOG === */}
          {tab === "catalog" && (
            <BuyerCatalogTab products={products} enabled={enabled} reload={load}/>
          )}

          {/* === DAILY ORDERS (merged with tracking/movement monitor) === */}
          {tab === "orders" && (
            <BuyerDailyOrdersTab
              orders={orders}
              movement={movement}
              hasStores={hasStores}
              suppliers={suppliers}
              onSeedDemo={seedDemoOrders}
              onCsvUploadSuccess={() => load()}
              onRescan={async () => {
                const r = await api.post("/dropship/movement-monitor/refresh");
                setMovement(r.data || {});
                load();
              }}
              search={orderSearch} setSearch={setOrderSearch}
              filters={orderFilters} setFilters={setOrderFilters}
              onOpenTrack={setTrackOpen}
            />
          )}

          {/* === INVOICES === */}
          {tab === "invoices" && (
            <BuyerInvoicesPanel/>
          )}

          {/* === CLAIMS · also hosts the Returns toggle === */}
          {tab === "claims" && (
            <ClaimsOrReturns claims={claims} orders={orders} reload={load}/>
          )}

          {/* === ANALYTICS === */}
          {tab === "analytics" && (
            <BuyerAnalyticsPanel/>
          )}
        </div>
      </div>

      {/* Connect Wizard — generic, driven by /api/dropship/channels/catalog
          field metadata. Handles all 8 connectors with one component. */}
      <ChannelSetupWizard
        open={connectOpen}
        onOpenChange={setConnectOpen}
        onConnected={() => load()}
      />

      <LiveTrackingDialog order={trackOpen} onClose={() => setTrackOpen(null)}/>
    </DropshipWorkspaceShell>
  );
}


// ─────────────────────────────────────────────────────────────────────
// BuyerConnectStoreTab — A buyer can connect MANY Shopify storefronts;
// each storefront keeps its own Schedule (timezone · cutoff · invoice
// generation time). Suppliers are account-level (shared across all
// storefronts) so the bilateral Agreement renders once per supplier.
// ─────────────────────────────────────────────────────────────────────
function BuyerConnectStoreTab({ stores, hasStores, onSeedDemo, onOpenConnect, onResync, resyncing, suppliers, reload }) {
  const supplier = suppliers && suppliers[0]; // 1 buyer → 1 supplier (today's design)
  // Per-store Schedule drawer state — tracks WHICH store is open.
  const [schedStoreId, setSchedStoreId] = useState(null);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const schedStore = stores.find(s => s.id === schedStoreId);

  return (
    <div className="space-y-8">
      {/* How it works — always visible, feels on-brand */}
      <div>
        <div className="text-xs mono uppercase tracking-[0.18em] text-foreground/55 mb-4">How dropship works for you</div>
        <div className="grid lg:grid-cols-5 gap-2 items-start">
          {[
            { n: 1, l: "Find a supplier",       d: "Browse suppliers offering dropship, or open a chat with one you already know." },
            { n: 2, l: "Sign the agreement",    d: "Shipping SLA, returns, claims, payment terms — both sides sign. Locked once active." },
            { n: 3, l: "Mirror their catalog",  d: "Supplier's products appear here with their real MSRP/MAP/wholesale. Pick what to sell and set your retail margin." },
            { n: 4, l: "Connect your channel",  d: "Shopify, Woo, Amazon, Walmart, TikTok, custom site, or scheduled CSV — verqo orchestrates orders both ways." },
            { n: 5, l: "Daily invoice",         d: "One consolidated invoice daily. Net-30 terms or prepay wallet." },
          ].map((s, i, arr) => (
            <div key={s.n} className="flex flex-col gap-2.5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-secondary text-foreground/70 grid place-items-center text-xs font-medium">{s.n}</div>
                {i < arr.length - 1 && <div className="flex-1 h-px bg-border"/>}
              </div>
              <div className="text-sm font-medium">{s.l}</div>
              <div className="text-xs text-foreground/55">{s.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Suppliers offering dropship — the missing discovery surface */}
      <BuyerDropshipSupplierDiscovery />

      {/* ────── Connected channels — one card per dropship_stores row.
           Channel kind comes from `platform` field (shopify / woocommerce
           / amazon_sp / walmart / tiktok_shop / custom_webhook /
           csv_scheduled). Each card surfaces the orchestrator's last-sync
           result + a Force Resync button wired to /dropship/stores/{id}/resync. */}
      {hasStores ? (
        <>
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs mono uppercase tracking-[0.18em] text-foreground/55">
                Connected channels ({stores.length})
              </div>
              <Button onClick={onOpenConnect} variant="outline"
                data-testid="add-another-store-btn"
                className="rounded-full h-9 px-4 text-[12px] mono uppercase tracking-[0.14em]">
                <Plus className="w-3.5 h-3.5 mr-1.5"/>Add another channel
              </Button>
            </div>
            <div className="space-y-3">
              {stores.map(store => {
                const isResyncing = !!(resyncing || {})[store.id];
                const kind = store.platform || "shopify";
                const kindLabel = ({
                  "shopify": "Shopify",
                  "shopify_custom_app": "Shopify",
                  "woocommerce": "WooCommerce",
                  "amazon_sp": "Amazon Seller",
                  "walmart": "Walmart",
                  "tiktok_shop": "TikTok Shop",
                  "custom_webhook": "Custom site",
                  "csv_scheduled": "Scheduled CSV",
                })[kind] || kind;
                // Compute "synced X ago" relative-time pill.
                const last = store.last_sync_at;
                const lastStats = store.last_sync_stats || {};
                let agoLabel = "—";
                if (last) {
                  const mins = Math.floor((Date.now() - new Date(last).getTime()) / 60000);
                  agoLabel = mins < 1 ? "just now"
                    : mins < 60 ? `${mins}m ago`
                    : mins < 1440 ? `${Math.floor(mins/60)}h ago`
                    : `${Math.floor(mins/1440)}d ago`;
                }
                const hasError = !!store.last_sync_error;
                return (
                <div key={store.id} className="rounded-2xl border border-border bg-white p-5"
                     data-testid={`connected-store-card-${store.id}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-11 h-11 rounded-xl bg-secondary grid place-items-center shrink-0">
                      <Store className="w-5 h-5 text-foreground/60"/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="display text-xl truncate">{store.display_name || kindLabel}</div>
                        <span className="text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-foreground/[0.06] text-foreground/65 border border-border" data-testid={`store-kind-${store.id}`}>
                          {kindLabel}
                        </span>
                      </div>
                      <div className="text-[12px] mono text-foreground/55 truncate">{store.store_url || store.csv_url || store.inbound_webhook_url || ""}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Last sync pill — green when fresh & healthy, amber when stale, red on error */}
                      <span className={`inline-flex items-center gap-1.5 text-[11px] mono px-2 py-1 rounded-full border ${
                        hasError ? "bg-rose-50 text-rose-700 border-rose-200"
                                  : last ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                          : "bg-secondary text-foreground/55 border-border"
                      }`} title={hasError ? store.last_sync_error : undefined}
                          data-testid={`store-sync-pill-${store.id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          hasError ? "bg-rose-500" : last ? "bg-emerald-500" : "bg-foreground/30"
                        }`}/>
                        synced {agoLabel}
                        {(lastStats.new ?? null) !== null && (
                          <span className="opacity-70">· {lastStats.new} new / {lastStats.deduped || 0} de-dup</span>
                        )}
                      </span>
                      <button onClick={() => onResync && onResync(store.id, 24)}
                        disabled={isResyncing}
                        title="Force re-pull orders from the last 24 hours"
                        data-testid={`force-resync-${store.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full h-9 px-3 text-[11px] mono uppercase tracking-[0.14em] border border-border hover:bg-secondary disabled:opacity-50">
                        <RefreshCw className={`w-3 h-3 ${isResyncing ? "animate-spin" : ""}`}/>{isResyncing ? "Syncing…" : "Resync"}
                      </button>
                      <button onClick={() => setSchedStoreId(store.id)}
                        data-testid={`open-store-settings-${store.id}`}
                        title="Schedule for this channel"
                        className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] bg-foreground text-background hover:bg-foreground/90">
                        <SettingsIcon className="w-3.5 h-3.5"/>Settings
                      </button>
                    </div>
                  </div>
                  {hasError && (
                    <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50/50 px-3 py-2 text-[11.5px] text-rose-900" data-testid={`store-sync-error-${store.id}`}>
                      <strong>Last sync failed:</strong> {store.last_sync_error}
                    </div>
                  )}
                  <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-4 text-[12.5px]">
                    <div>
                      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Products synced</div>
                      <div className="mt-0.5">{store.products_synced || 0}</div>
                    </div>
                    <div>
                      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Last sync (full)</div>
                      <div className="mt-0.5">{last ? new Date(last).toLocaleString() : "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Fill rate</div>
                      <div className="mt-0.5">{store.fill_rate ?? "—"}%</div>
                    </div>
                    <div>
                      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Schedule</div>
                      <div className="mt-0.5 truncate">
                        {(store.timezone || "America/New_York").split("/").pop()?.replace(/_/g, " ")} · cut {_fmt12(store.confirm_cutoff_time || "11:00")}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* ────── Supplier + Sync handshake — account-level, shared across all storefronts ────── */}
          {supplier && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs mono uppercase tracking-[0.18em] text-foreground/55">Your supplier</div>
                <button onClick={() => setAgreementOpen(true)}
                  data-testid="open-agreement-drawer"
                  className="rounded-full h-9 px-4 text-[12px] mono uppercase tracking-[0.14em] border border-border hover:bg-secondary inline-flex items-center gap-1.5">
                  <FileSignature className="w-3.5 h-3.5"/>Agreement
                </button>
              </div>
              <div className="rounded-2xl border border-border bg-white p-5">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center shrink-0 mono font-medium">
                    {(supplier.company || supplier.name || "S").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="display text-xl truncate">{supplier.company || supplier.name}</div>
                    <div className="text-[12px] mono text-foreground/55 truncate">{supplier.email}</div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-700">
                    <Check className="w-3 h-3"/>Agreement active
                  </span>
                </div>
                <div className="mt-4">
                  <SyncHandshakeCard counterpartId={supplier.id} role="buyer"/>
                </div>
              </div>
            </div>
          )}

          {/* Demo / preview helpers */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={onSeedDemo} variant="outline" className="rounded-full h-9 px-4 text-[12.5px]" data-testid="seed-demo-orders-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5"/>Generate demo orders
            </Button>
            <Button onClick={async ()=>{
              try {
                const { data } = await api.post("/shopify/mock/inbound-order",
                  { seller_id: supplier?.id });
                if (data.ok) {
                  toast.success(`Simulated Shopify order created · ${data.orders_created} line(s) · external #${data.buyer_external_order_id?.slice(-4) || "?"}`);
                  reload();
                } else {
                  toast.error(`Simulator returned: ${data.reason || "unknown"}`);
                }
              } catch (e) {
                toast.error(e.response?.data?.detail || "Simulator failed");
              }
            }} variant="outline" className="rounded-full h-9 px-4 text-[12.5px] border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="sim-shopify-order-btn"
              title="Mock-mode only: fires a fake Shopify orders/create webhook so you can see the 3-ID flow end-to-end">
              <Sparkles className="w-3.5 h-3.5 mr-1.5"/>Simulate Shopify order
            </Button>
          </div>

          {/* Per-store Schedule drawer — opens scoped to the clicked store */}
          <Sheet open={!!schedStoreId} onOpenChange={(o) => !o && setSchedStoreId(null)}>
            <SheetContent side="right" className="sm:max-w-2xl w-full overflow-y-auto" data-testid="store-settings-sheet">
              <SheetHeader>
                <SheetTitle className="display text-2xl">
                  Storefront settings{schedStore ? ` · ${schedStore.display_name}` : ""}
                </SheetTitle>
                <SheetDescription>
                  Per-storefront schedule (timezone · cutoff · invoice time). Applies instantly — no supplier approval needed.
                </SheetDescription>
              </SheetHeader>

              {schedStore && (
                <div className="mt-6 rounded-2xl border border-border bg-secondary/30 p-5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Clock className="w-4 h-4 text-foreground/65"/>
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/70">Schedule for {schedStore.display_name}</div>
                  </div>
                  <DropshipScheduleEditor store={schedStore} onSaved={reload}/>
                </div>
              )}
            </SheetContent>
          </Sheet>

          {/* Agreement drawer — account-level, supplier-scoped */}
          <Sheet open={agreementOpen} onOpenChange={setAgreementOpen}>
            <SheetContent side="right" className="sm:max-w-3xl w-full overflow-y-auto" data-testid="agreement-sheet">
              <SheetHeader>
                <SheetTitle className="display text-2xl">Agreement{supplier ? ` · ${supplier.company || supplier.name}` : ""}</SheetTitle>
                <SheetDescription>
                  Bilateral contract with your supplier. You decide the workflow mode; both sides sign the MSA, return / delay / damage policies, and deduction rules. Applies to ALL your storefronts.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 rounded-2xl border-2 border-indigo-200 bg-indigo-50/30 p-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <FileSignature className="w-4 h-4 text-indigo-700"/>
                  <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-indigo-800">Bilateral · all storefronts</div>
                </div>
                {supplier ? (
                  <TermsEditor counterpartId={supplier.id} role="buyer"/>
                ) : (
                  <div className="rounded-xl border border-dashed border-border bg-white px-4 py-6 text-center text-[12.5px] text-foreground/55">
                    No supplier connected yet. The Agreement section unlocks once your first order lands from the supplier.
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white border border-border grid place-items-center mx-auto">
            <Store className="w-6 h-6 text-foreground/40"/>
          </div>
          <div className="display text-2xl mt-4">No store connected yet</div>
          <p className="text-sm text-foreground/55 mt-1.5 max-w-md mx-auto">
            Connect your first Shopify storefront. You can add more later.
          </p>
          <Button onClick={onOpenConnect} className="mt-5 rounded-full h-11 px-5 bg-foreground text-background hover:bg-foreground/90"
            data-testid="connect-empty-cta">
            <Plus className="w-4 h-4 mr-1.5"/>Connect a channel
          </Button>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// BuyerCatalogTab — products the buyer can push to their Shopify store.
// Custom-margin input per row (default 100% on top of wholesale) lets the
// buyer decide their retail price with a live computed margin percentage.
// No "enabled" toggle — products stream in from all connected suppliers;
// the buyer sends one to their Shopify with a single CTA.
// ─────────────────────────────────────────────────────────────────────
function BuyerCatalogTab({ products, enabled, reload }) {
  const [drafts, setDrafts] = useState({}); // { [productId]: retail_price }
  const [saving, setSaving] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [statusChip, setStatusChip] = useState("all");

  const getRetail = (p) => {
    const d = drafts[p.id];
    if (d != null) return d;
    return p.retail_price || (Number(p.wholesale_cost || 0) * 2);
  };
  const setRetail = (pid, v) => setDrafts(d => ({ ...d, [pid]: v }));
  const marginPct = (p) => {
    const retail = Number(getRetail(p)) || 0;
    const cost = Number(p.wholesale_cost) || 0;
    if (retail <= 0) return 0;
    return Math.round(((retail - cost) / retail) * 100);
  };
  const isDirty = (p) => {
    const d = drafts[p.id];
    return d != null && Number(d) !== Number(p.retail_price || 0);
  };

  const supplierOptions = useMemo(() => {
    const seen = new Set();
    products.forEach(p => { if (p.seller_name) seen.add(p.seller_name); });
    return [...seen].map(v => ({ value: v, label: v }));
  }, [products]);

  // Sync-rules variant — same suppliers, but values are seller_ids (the
  // rules engine matches on seller_id, not seller_name).
  const supplierOptionsForRules = useMemo(() => {
    const seen = new Map();
    products.forEach(p => {
      if (p.seller_id && p.seller_name) seen.set(p.seller_name, p.seller_id);
    });
    return [...seen].map(([name, id]) => ({ value: id, label: name }));
  }, [products]);

  // Categories list for the auto-pull rules dialog (chips). Mirrors the
  // shape SyncRulesPanel expects: [{value, label}] derived from the
  // catalog the buyer is streaming in from connected suppliers.
  const categoryOptionsForRules = useMemo(() => {
    const seen = new Set();
    products.forEach(p => { if (p.category) seen.add(p.category); });
    return [...seen].map(v => ({ value: v, label: v }));
  }, [products]);

  const filtered = useMemo(() => {
    let xs = products;
    if (statusChip === "in_store")  xs = xs.filter(p => p.enabled);
    if (statusChip === "available") xs = xs.filter(p => !p.enabled);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(p =>
        (p.name || p.title || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.seller_name || "").toLowerCase().includes(q)
      );
    }
    const F = filters || {};
    if (F.supplier?.values?.length) xs = xs.filter(p => F.supplier.values.includes(p.seller_name));
    if (F.inStore?.on != null)      xs = xs.filter(p => F.inStore.on ? !!p.enabled : !p.enabled);
    if (F.wholesale?.value != null) {
      xs = xs.filter(p => {
        const v = Number(p.wholesale_cost) || 0;
        if (F.wholesale.op === "gt") return v > Number(F.wholesale.value);
        if (F.wholesale.op === "lt") return v < Number(F.wholesale.value);
        if (F.wholesale.op === "between") return v >= Number(F.wholesale.min) && v <= Number(F.wholesale.max);
        return true;
      });
    }
    if (F.margin?.value != null) {
      xs = xs.filter(p => {
        const m = marginPct(p);
        if (F.margin.op === "gt") return m > Number(F.margin.value);
        if (F.margin.op === "lt") return m < Number(F.margin.value);
        if (F.margin.op === "between") return m >= Number(F.margin.min) && m <= Number(F.margin.max);
        return true;
      });
    }
    return xs;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, statusChip, search, filters, drafts]);

  const toggleOne = (id) => setSelected(s => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const clearSelection = () => setSelected(new Set());

  const save = async (p) => {
    const retail = Number(getRetail(p));
    if (retail <= 0) { toast.error("Retail must be > 0"); return; }
    setSaving(s => ({ ...s, [p.id]: true }));
    try {
      await api.patch(`/dropship/products/${p.id}`, { retail_price: retail, enabled: true });
      toast.success("Added to your store");
      setDrafts(d => { const n = { ...d }; delete n[p.id]; return n; });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(s => ({ ...s, [p.id]: false })); }
  };
  const remove = async (p) => {
    try {
      await api.patch(`/dropship/products/${p.id}`, { enabled: false });
      toast.success("Removed from store");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const bulk = async (enabled) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/dropship/products/bulk-toggle", { product_ids: ids, enabled });
      toast.success(
        enabled
          ? `Added ${data.added} to store${data.already_in ? ` · ${data.already_in} already in` : ""}`
          : `Removed ${data.removed} from store`
      );
      clearSelection();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
    finally { setBulkBusy(false); }
  };

  const avgMargin = (() => {
    const arr = enabled.filter(p => p.retail_price && p.wholesale_cost);
    if (!arr.length) return null;
    const pct = arr.reduce((a, p) => a + ((p.retail_price - p.wholesale_cost) / p.retail_price * 100), 0) / arr.length;
    return Math.round(pct);
  })();

  const shown = filtered.slice(0, 120);
  const shownIds = shown.map(p => p.id);
  const allSelected  = shownIds.length > 0 && shownIds.every(id => selected.has(id));
  const someSelected = shownIds.some(id => selected.has(id));
  const toggleAll = () => setSelected(s => {
    const n = new Set(s);
    if (allSelected) shownIds.forEach(id => n.delete(id));
    else             shownIds.forEach(id => n.add(id));
    return n;
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-border bg-white p-5">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">In my store</div>
          <div className="display text-3xl mt-1.5">{enabled.length}</div>
          <div className="text-[11px] text-foreground/55 mt-1">of {products.length} available</div>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Avg margin</div>
          <div className="display text-3xl mt-1.5">{avgMargin != null ? `${avgMargin}%` : "—"}</div>
          <div className="text-[11px] text-foreground/55 mt-1">across added products</div>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Supplier catalog</div>
          <div className="display text-3xl mt-1.5">{products.length}</div>
          <div className="text-[11px] text-foreground/55 mt-1">products streaming in</div>
        </div>
      </div>

      {/* Auto-pull rules — saved filters that auto-add matching supplier products */}
      <SyncRulesPanel role="buyer"
        categoryOptions={categoryOptionsForRules}
        supplierOptions={supplierOptionsForRules}
        onChange={reload}/>

      {/* Status chips */}
      <div className="flex gap-1.5 flex-wrap" data-testid="buyer-catalog-chips">
        {[
          { k: "all",       l: "All",          c: products.length },
          { k: "in_store",  l: "In my store",  c: enabled.length },
          { k: "available", l: "Not added",    c: products.length - enabled.length },
        ].map(f => (
          <button key={f.k} onClick={() => setStatusChip(f.k)}
            data-testid={`buyer-catalog-chip-${f.k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[12.5px] border transition-colors ${statusChip === f.k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>
            {f.l}
            <span className={`text-[10.5px] mono px-1.5 rounded-full ${statusChip === f.k ? "bg-background/20" : "bg-secondary"}`}>{f.c}</span>
          </button>
        ))}
      </div>

      <SearchAndFilter
        page="buyer_dropship_catalog"
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onChange={setFilters}
        totalCount={filtered.length}
        facets={[
          { id: "supplier",  label: "Supplier",     icon: Users,      type: "multi", options: supplierOptions },
          { id: "inStore",   label: "In my store",  icon: Store,      type: "boolean" },
          { id: "wholesale", label: "Wholesale $",  icon: DollarSign, type: "numeric", numericLabel: "$" },
          { id: "margin",    label: "Margin %",     icon: Activity,   type: "numeric", numericLabel: "%" },
        ]}
      />

      {/* Bulk-action bar */}
      {selected.size > 0 && (
        <div className="sticky top-3 z-20 rounded-2xl border border-foreground/20 bg-foreground text-background shadow-lg px-5 py-3 flex items-center gap-4 flex-wrap" data-testid="buyer-catalog-bulk-bar">
          <div className="text-[13px] font-medium">{selected.size} selected</div>
          <div className="ml-auto flex items-center gap-2">
            <button disabled={bulkBusy} onClick={() => bulk(true)}
              data-testid="buyer-catalog-bulk-add"
              className="rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] bg-background text-foreground hover:bg-background/90 disabled:opacity-50 inline-flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5"/>Add to store
            </button>
            <button disabled={bulkBusy} onClick={() => bulk(false)}
              data-testid="buyer-catalog-bulk-remove"
              className="rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] bg-background/10 text-background border border-background/30 hover:bg-background/20 disabled:opacity-50 inline-flex items-center gap-1.5">
              Remove from store
            </button>
            <button onClick={clearSelection} data-testid="buyer-catalog-bulk-clear"
              className="text-[11.5px] mono uppercase tracking-[0.14em] text-background/70 hover:text-background ml-2">
              Clear
            </button>
          </div>
        </div>
      )}

      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-12 text-center" data-testid="buyer-catalog-empty">
          <Package className="w-8 h-8 mx-auto text-foreground/30 mb-2"/>
          {products.length === 0 ? (
            <>
              <div className="text-sm font-medium">No supplier catalogs yet</div>
              <div className="text-xs text-foreground/55 mt-1 max-w-md mx-auto">
                Sign a dropship agreement with a supplier and their entire catalog mirrors here automatically — with their real wholesale, MSRP and MAP pricing.
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium">No products match these filters</div>
              <div className="text-xs text-foreground/55 mt-1">Clear a chip or filter above to see more.</div>
            </>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <div className="grid grid-cols-[36px_1fr_150px_130px_90px_160px] gap-3 px-5 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
            <div className="flex items-center">
              <input type="checkbox"
                ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                checked={allSelected}
                onChange={toggleAll}
                data-testid="buyer-catalog-select-all"
                className="w-4 h-4 cursor-pointer accent-foreground"
              />
            </div>
            <div>Product</div><div className="text-right">Cost · MSRP / MAP</div><div className="text-right">Your retail</div><div className="text-right">Margin</div><div className="text-right">Action</div>
          </div>
          <div className="divide-y divide-border">
            {shown.map(p => {
              const retail = getRetail(p);
              const inStore = !!p.enabled;
              const dirty = isDirty(p);
              const busy = !!saving[p.id];
              return (
                <div key={p.id} className={`grid grid-cols-[36px_1fr_150px_130px_90px_160px] gap-3 px-5 py-3 items-center text-[13px] ${selected.has(p.id) ? "bg-indigo-50/50" : ""}`} data-testid={`ds-product-${p.id}`}>
                  <div className="flex items-center">
                    <input type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleOne(p.id)}
                      data-testid={`buyer-catalog-select-${p.id}`}
                      className="w-4 h-4 cursor-pointer accent-foreground"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.name || p.title}</div>
                    <div className="text-[11px] mono text-foreground/55 truncate">{p.sku} · {p.seller_name}</div>
                  </div>
                  <div className="text-right" data-testid={`ds-cost-${p.id}`}>
                    <div className="mono font-medium text-foreground/85">{money(p.wholesale_unit_price ?? p.wholesale_cost ?? 0)}</div>
                    {p.dropship_handling_fee > 0 && (
                      <div className="text-[10px] mono text-amber-700">+ {money(p.dropship_handling_fee)} handling</div>
                    )}
                    <div className="text-[10px] mono text-foreground/55 mt-0.5">
                      MSRP {money(p.msrp || 0)}{p.map_price ? <> · <span className="text-rose-700/70">MAP {money(p.map_price)}</span></> : null}
                    </div>
                  </div>
                  <div className="flex items-center justify-end">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-foreground/50 text-[12px]">$</span>
                      <Input
                        type="number" step="0.01" min="0"
                        value={Number(retail).toFixed(2)}
                        onChange={e => setRetail(p.id, e.target.value)}
                        data-testid={`ds-retail-${p.id}`}
                        className="w-[110px] h-9 pl-5 text-right mono text-[12.5px]"/>
                    </div>
                  </div>
                  <div className={`text-right mono font-medium ${marginPct(p) >= 50 ? "text-emerald-700" : marginPct(p) >= 20 ? "text-foreground" : "text-rose-700"}`}>
                    {marginPct(p)}%
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    {dirty ? (
                      <Button onClick={() => save(p)} disabled={busy}
                        data-testid={`ds-save-retail-${p.id}`}
                        className="rounded-full h-8 px-3 text-[11px] bg-foreground text-background hover:bg-foreground/90">
                        {busy ? "…" : inStore ? "Update" : "Add to store"}
                      </Button>
                    ) : inStore ? (
                      <>
                        <span className="text-[10px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">In store</span>
                        <button onClick={() => remove(p)} data-testid={`ds-remove-${p.id}`}
                          className="text-[10px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border border-border text-foreground/65 hover:bg-secondary">
                          Remove
                        </button>
                      </>
                    ) : (
                      <Button onClick={() => save(p)} disabled={busy}
                        data-testid={`ds-add-${p.id}`}
                        variant="outline"
                        className="rounded-full h-8 px-3 text-[11px]">
                        Add to store
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// BuyerTermsTab — workflow section (per-store schedule) + Signed terms
// per supplier (role-aware TermsEditor with Full Auto credit gate).
// Same structure as the seller's Terms tab but scoped buyer-side.
// ─────────────────────────────────────────────────────────────────────
function BuyerTermsTab({ stores, reload, suppliers, activeSupplierId, setActiveSupplierId }) {
  return (
    <div className="space-y-8">
      {/* Section 1 — Your workflow */}
      <section>
        <div className="rounded-xl px-4 py-3 border border-border bg-foreground/[0.03] mb-4 flex items-start gap-2">
          <Settings className="w-4 h-4 mt-0.5 shrink-0 text-foreground/60"/>
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground">Your workflow</div>
            <div className="text-[12px] text-foreground/65 mt-0.5">
              Per-store cutoff and invoice-generation schedule. Changes apply instantly — no supplier approval needed.
            </div>
          </div>
        </div>
        <DropshipScheduleEditor stores={stores} onSaved={reload}/>
      </section>

      {/* Section 2 — Signed terms per supplier */}
      <section>
        <div className="rounded-xl px-4 py-3 border-2 border-indigo-200 bg-indigo-50/40 mb-4 flex items-start gap-2" data-testid="buyer-signed-terms-header">
          <FileSignature className="w-4 h-4 mt-0.5 shrink-0 text-indigo-700"/>
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-indigo-800">Signed terms with your supplier</div>
            <div className="text-[12px] text-indigo-900/75 mt-0.5">
              You decide the workflow mode (Full Auto vs Daily Confirm) — your supplier follows. Responsibilities, SLA, and contract are bilateral — both parties must sign.
            </div>
          </div>
        </div>
        {suppliers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-secondary/20 px-6 py-12 text-center">
            <Users className="w-8 h-8 mx-auto text-foreground/30 mb-2"/>
            <div className="text-sm font-medium">No connected supplier yet</div>
            <div className="text-xs text-foreground/55 mt-1">Once your first dropship order lands, your supplier shows up here and you can negotiate terms.</div>
          </div>
        ) : suppliers.length === 1 ? (
          // 1:1 case — the common one. Skip the picker entirely and render
          // a compact supplier header + the full TermsEditor below.
          <div data-testid="buyer-suppliers-single">
            <div className="rounded-2xl border border-border bg-white px-4 py-3 mb-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-secondary grid place-items-center shrink-0">
                <Users className="w-4 h-4 text-foreground/60"/>
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-medium truncate">{suppliers[0].company || suppliers[0].name}</div>
                <div className="text-[11px] text-foreground/55 truncate">{suppliers[0].email}</div>
              </div>
              <span className="ml-auto text-[10px] mono uppercase tracking-[0.14em] text-emerald-700 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>Connected
              </span>
            </div>
            <TermsEditor counterpartId={suppliers[0].id} role="buyer"/>
          </div>
        ) : (
          // Multi-supplier case kept for completeness (rare in dropship).
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
            <aside className="rounded-2xl border border-border bg-white p-2 space-y-1 self-start" data-testid="buyer-suppliers-list">
              {suppliers.map(s => (
                <button
                  key={s.id}
                  onClick={() => setActiveSupplierId(s.id)}
                  data-testid={`buyer-supplier-${s.id}`}
                  className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center justify-between ${activeSupplierId === s.id ? "bg-secondary" : "hover:bg-secondary/50"}`}
                >
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium truncate">{s.company || s.name}</div>
                    <div className="text-[11px] text-foreground/55 truncate">{s.email}</div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-foreground/30"/>
                </button>
              ))}
            </aside>
            <section>
              {activeSupplierId && <TermsEditor counterpartId={activeSupplierId} role="buyer"/>}
            </section>
          </div>
        )}
      </section>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// BuyerDailyOrdersTab — unified customer-orders feed.
// Now includes the full tracking & movement monitor that used to live in
// its own tab: live tracking dialog, stalled/no-movement red-flagging,
// movement KPIs, and on-demand rescan. Everything a buyer needs to track
// in one scrollable surface.
// ─────────────────────────────────────────────────────────────────────
function BuyerDailyOrdersTab({ orders, movement, hasStores, suppliers, onSeedDemo, onRescan, onCsvUploadSuccess, search, setSearch, filters, setFilters, onOpenTrack }) {
  const [statusChip, setStatusChip] = useState("all");
  const [rescanning, setRescanning] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // upload_batch_id filter (set from the CSV history drawer's "Filter to this batch" CTA)
  const [batchFilter, setBatchFilter] = useState(null);
  // Pre-select a supplier when the user clicks "Re-upload" from the
  // history drawer — passed into CsvUploadDialog as initialSellerId.
  const [reuploadSellerId, setReuploadSellerId] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  // SLA Extension — buyer approves or rejects the seller's request
  const [slaReviewOpen, setSlaReviewOpen] = useState(null);
  // File-claim dialog — buyer surfaces this from any eligible order row
  const [fileClaimFor, setFileClaimFor] = useState(null);
  const respondSla = async (order, action) => {
    try {
      await api.patch(`/dropship/orders/${order.id}/sla-extension/respond`, { action });
      if (action === "approve") {
        toast.success(`Extension approved — SLA counts as on-time for ${order.code}`);
      } else {
        toast.message(`Extension rejected — ${order.code} cancelled and refunded`);
      }
      setSlaReviewOpen(null);
      // Parent holds the orders list; trigger a movement refresh + reload.
      if (onRescan) onRescan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not respond");
    }
  };
  const approveSla = (o) => respondSla(o, "approve");
  const rejectSla  = (o) => respondSla(o, "reject");

  const isToday      = (o) => (o.created_at || "").slice(0, 10) === today;
  const isInTransit  = (o) => ["shipped", "in_transit"].includes(o.status);
  const isAwaitPay   = (o) => o.status === "awaiting_payment";
  // Movement classification — only applies to shipped/in_transit orders;
  // backend stamps `movement_status`. Default to "moving" for shipped
  // orders that don't yet have a monitor reading.
  const ms = (o) => isInTransit(o) ? (o.movement_status || "moving") : null;
  const isStalled    = (o) => ms(o) === "stalled";
  const isNoMovement = (o) => ms(o) === "no_movement";

  const supplierOptions = useMemo(() => {
    const seen = new Set();
    orders.forEach(o => { if (o.seller_name) seen.add(o.seller_name); });
    return [...seen].map(v => ({ value: v, label: v }));
  }, [orders]);
  const storeOptions = useMemo(() => {
    const seen = new Set();
    orders.forEach(o => { if (o.store_name) seen.add(o.store_name); });
    return [...seen].map(v => ({ value: v, label: v }));
  }, [orders]);

  const filtered = useMemo(() => {
    let xs = orders;
    if (statusChip === "today")             xs = xs.filter(isToday);
    if (statusChip === "in_transit")        xs = xs.filter(isInTransit);
    if (statusChip === "stalled")           xs = xs.filter(isStalled);
    if (statusChip === "no_movement")       xs = xs.filter(isNoMovement);
    if (statusChip === "awaiting_payment")  xs = xs.filter(isAwaitPay);
    if (batchFilter) xs = xs.filter(o => o.upload_batch_id === batchFilter);
    if (search?.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(o =>
        (o.code || "").toLowerCase().includes(q) ||
        (o.customer_name || "").toLowerCase().includes(q) ||
        (o.product_name || "").toLowerCase().includes(q) ||
        (o.seller_name || "").toLowerCase().includes(q) ||
        (o.tracking || "").toLowerCase().includes(q) ||
        (o.buyer_external?.order_id || "").toLowerCase().includes(q) ||
        (o.buyer_external?.order_number || "").toLowerCase().includes(q) ||
        (o.seller_external?.order_id || "").toLowerCase().includes(q) ||
        (o.seller_external?.order_number || "").toLowerCase().includes(q)
      );
    }
    const F = filters || {};
    if (F.supplier?.values?.length) xs = xs.filter(o => F.supplier.values.includes(o.seller_name));
    if (F.store?.values?.length)    xs = xs.filter(o => F.store.values.includes(o.store_name));
    if (F.status?.values?.length)   xs = xs.filter(o => F.status.values.includes(o.status || "pending"));
    if (F.movement?.values?.length) xs = xs.filter(o => F.movement.values.includes(ms(o) || "none"));
    if (F.hasTracking?.on != null)  xs = xs.filter(o => F.hasTracking.on ? !!o.tracking : !o.tracking);
    if (F.hours?.value != null) {
      xs = xs.filter(o => {
        const v = Number(o.movement_hours);
        if (!Number.isFinite(v)) return false;
        if (F.hours.op === "gt") return v > Number(F.hours.value);
        if (F.hours.op === "lt") return v < Number(F.hours.value);
        if (F.hours.op === "between") return v >= Number(F.hours.min) && v <= Number(F.hours.max);
        return true;
      });
    }
    if (F.total?.value != null) {
      xs = xs.filter(o => {
        const v = Number(o.retail_price || 0) * Number(o.qty || 0);
        if (F.total.op === "gt") return v > Number(F.total.value);
        if (F.total.op === "lt") return v < Number(F.total.value);
        if (F.total.op === "between") return v >= Number(F.total.min) && v <= Number(F.total.max);
        return true;
      });
    }
    if (F.date?.value) xs = xs.filter(o => (o.created_at || "") >= F.date.value);
    return xs;
  }, [orders, search, filters, statusChip, today]); // eslint-disable-line react-hooks/exhaustive-deps

  const chipCount = {
    today:            orders.filter(isToday).length,
    in_transit:       orders.filter(isInTransit).length,
    stalled:          orders.filter(isStalled).length,
    no_movement:      orders.filter(isNoMovement).length,
    awaiting_payment: orders.filter(isAwaitPay).length,
  };

  const rescan = async () => {
    if (!onRescan) return;
    setRescanning(true);
    try { await onRescan(); toast.success("Movement re-scanned"); }
    catch { toast.error("Could not re-scan"); }
    finally { setRescanning(false); }
  };

  if (orders.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-12 text-center" data-testid="ds-orders-empty">
        <div className="w-14 h-14 rounded-2xl bg-white border border-border grid place-items-center mx-auto">
          <Store className="w-6 h-6 text-foreground/40"/>
        </div>
        <div className="display text-xl mt-4">No customer orders yet</div>
        <p className="text-sm text-foreground/55 mt-1.5 max-w-sm mx-auto">
          {hasStores
            ? "Customer orders from your Shopify will appear here in real time."
            : "Connect your Shopify store first to start receiving customer orders."}
        </p>
        {hasStores && (
          <Button onClick={onSeedDemo} variant="outline" className="mt-5 rounded-full h-10 px-4 text-[12.5px]" data-testid="seed-demo-orders-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5"/>Generate demo orders
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* KPI strip — order flow + movement red-flags */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiTile label="Today" value={chipCount.today} hint="Customer orders placed today"/>
        <KpiTile label="In transit" value={chipCount.in_transit} hint="Shipped · en route to your customer"/>
        <div className={`rounded-2xl border p-5 ${chipCount.stalled > 0 ? "bg-amber-50/60 border-amber-200" : "bg-white border-border"}`} data-testid="kpi-stalled">
          <div className={`text-[10px] mono uppercase tracking-[0.18em] ${chipCount.stalled > 0 ? "text-amber-700" : "text-foreground/55"}`}>Stalled · 48-96h</div>
          <div className={`display text-3xl mt-1.5 ${chipCount.stalled > 0 ? "text-amber-700" : ""}`}>{chipCount.stalled}</div>
          <div className="text-[11px] text-foreground/55 mt-1">No scans between 48-96h</div>
        </div>
        <div className={`rounded-2xl border p-5 ${chipCount.no_movement > 0 ? "bg-rose-50 border-rose-200" : "bg-white border-border"}`} data-testid="kpi-no-movement">
          <div className={`text-[10px] mono uppercase tracking-[0.18em] flex items-center gap-1 ${chipCount.no_movement > 0 ? "text-rose-700" : "text-foreground/55"}`}>
            {chipCount.no_movement > 0 && <AlertTriangle className="w-3 h-3"/>}No movement · 96h+
          </div>
          <div className={`display text-3xl mt-1.5 ${chipCount.no_movement > 0 ? "text-rose-700" : ""}`}>{chipCount.no_movement}</div>
          <div className="text-[11px] text-foreground/55 mt-1">File a claim if not resolved</div>
        </div>
        <KpiTile label="Awaiting payment" value={chipCount.awaiting_payment} hint={chipCount.awaiting_payment > 0 ? "Top up wallet to unblock FIFO" : "None blocked on wallet"}/>
      </div>

      {/* Quick chips — order-flow + movement red-flags */}
      <div className="flex gap-1.5 flex-wrap items-center" data-testid="buyer-orders-chips">
        {[
          { k: "all",              l: "All",              c: orders.length },
          { k: "today",            l: "Today",            c: chipCount.today },
          { k: "in_transit",       l: "In transit",       c: chipCount.in_transit },
          ...(chipCount.stalled > 0         ? [{ k: "stalled",          l: "Stalled",          c: chipCount.stalled,     tone: "amber" }] : []),
          ...(chipCount.no_movement > 0     ? [{ k: "no_movement",      l: "No movement",      c: chipCount.no_movement, tone: "rose"  }] : []),
          ...(chipCount.awaiting_payment > 0 ? [{ k: "awaiting_payment", l: "Awaiting payment", c: chipCount.awaiting_payment }] : []),
        ].map(f => (
          <button key={f.k} onClick={() => setStatusChip(f.k)} data-testid={`buyer-orders-chip-${f.k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[12.5px] border transition-colors ${
              statusChip === f.k
                ? "bg-foreground text-background border-foreground"
                : f.tone === "rose"  ? "border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100"
                : f.tone === "amber" ? "border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100"
                : "border-border hover:bg-secondary"
            }`}>
            {f.tone === "rose" && <AlertTriangle className="w-3 h-3"/>}
            {f.l}
            <span className={`text-[10.5px] mono px-1.5 rounded-full ${statusChip === f.k ? "bg-background/20" : "bg-white/60"}`}>{f.c}</span>
          </button>
        ))}
        <button onClick={rescan} disabled={rescanning}
          data-testid="buyer-orders-rescan"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[12.5px] border border-border hover:bg-secondary disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${rescanning ? "animate-spin" : ""}`}/>
          Re-scan movement
        </button>
        <button onClick={() => setHistoryOpen(true)}
          data-testid="buyer-orders-csv-history"
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[12.5px] border border-border hover:bg-secondary">
          <FileText className="w-3.5 h-3.5"/>
          CSV history
        </button>
        <button onClick={() => setCsvOpen(true)}
          data-testid="buyer-orders-upload-csv"
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[12.5px] bg-foreground text-background hover:bg-foreground/90">
          <Upload className="w-3.5 h-3.5"/>
          Upload CSV
        </button>
      </div>

      {batchFilter && (
        <div className="rounded-2xl border border-foreground bg-foreground/5 px-4 py-2.5 flex items-center gap-2 text-[12.5px]" data-testid="active-batch-filter">
          <Filter className="w-3.5 h-3.5 text-foreground/70"/>
          <span>Showing only orders from CSV upload <span className="mono text-foreground/55">{batchFilter.slice(0, 8)}</span></span>
          <button onClick={() => setBatchFilter(null)}
                  data-testid="clear-batch-filter-inline"
                  className="ml-auto text-[11px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border border-foreground bg-foreground text-background hover:bg-foreground/90 inline-flex items-center gap-1">
            Clear<X className="w-2.5 h-2.5"/>
          </button>
        </div>
      )}

      <SearchAndFilter
        page="buyer_dropship_orders"
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onChange={setFilters}
        totalCount={filtered.length}
        facets={[
          { id: "supplier",    label: "Supplier",  icon: Users,      type: "multi", options: supplierOptions },
          { id: "store",       label: "Store",     icon: Store,      type: "multi", options: storeOptions },
          { id: "status",      label: "Status",    icon: Activity,   type: "multi", options: [
            { value: "pending",    label: "Pending" },
            { value: "shipped",    label: "Shipped" },
            { value: "in_transit", label: "In transit" },
            { value: "delivered",  label: "Delivered" },
            { value: "awaiting_payment", label: "Awaiting payment" },
            { value: "cancelled",  label: "Cancelled" },
          ]},
          { id: "movement",    label: "Movement",  icon: Truck,      type: "multi", options: [
            { value: "stalled",      label: "Stalled" },
            { value: "no_movement",  label: "No movement" },
          ]},
          { id: "hasTracking", label: "Has tracking", icon: MapPin,  type: "boolean" },
          { id: "hours",       label: "Hours since move", icon: Clock, type: "numeric" },
          { id: "total",       label: "Order total", icon: DollarSign, type: "numeric", numericLabel: "$" },
          { id: "date",        label: "Placed after", icon: Calendar, type: "date" },
        ]}
      />

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/20 px-6 py-12 text-center">
          <Inbox className="w-8 h-8 mx-auto text-foreground/30 mb-2"/>
          <div className="text-sm font-medium">No orders match these filters</div>
          <div className="text-xs text-foreground/55 mt-1">Clear a chip or facet above to see more.</div>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="ds-orders-list">
          <div className="grid grid-cols-[150px_1fr_140px_80px_90px_220px] gap-3 px-5 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
            <div>Order · External IDs</div><div>Customer · Product</div><div>Supplier</div><div className="text-right">Qty</div><div className="text-right">Total</div><div className="text-right">Status · Movement</div>
          </div>
          <div className="divide-y divide-border">
            {filtered.map(o => {
              const total = (o.retail_price || 0) * (o.qty || 0);
              const awaitingPay = o.status === "awaiting_payment";
              const mv = ms(o);
              const slaExt = o.sla_extension;
              const isSlaApproved = slaExt?.status === "approved";
              const isSlaPending  = slaExt?.status === "pending";
              const rowTone =
                isSlaApproved            ? "bg-emerald-50/40 border-l-4 border-l-emerald-500" :
                isSlaPending             ? "bg-amber-50/60 border-l-4 border-l-amber-500" :
                awaitingPay              ? "bg-rose-50/40 border-l-4 border-l-rose-500" :
                mv === "no_movement"     ? "bg-rose-50 border-l-4 border-l-rose-500" :
                mv === "stalled"         ? "bg-amber-50/50 border-l-4 border-l-amber-500" : "";
              const hrs = o.movement_hours;
              return (
                <div key={o.id} className={`grid grid-cols-[150px_1fr_140px_80px_90px_220px] gap-3 px-5 py-3 items-center text-[13px] ${rowTone}`} data-testid={`ds-order-${o.code}`}>
                  <OrderIdTriple order={o} role="buyer"/>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{o.customer_name}</div>
                    <div className="text-[11px] text-foreground/55 truncate flex items-center gap-1.5 flex-wrap">
                      <span className="truncate">{o.product_name}</span>
                      {o.tracking && (
                        <button onClick={() => onOpenTrack(o)}
                          data-testid={`buyer-track-${o.code}`}
                          title="View live carrier tracking"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-border bg-white hover:bg-secondary transition-colors text-foreground align-middle mono text-[10.5px]">
                          <MapPin className="w-2.5 h-2.5 text-foreground/60"/>
                          {o.tracking}
                        </button>
                      )}
                      {mv && mv !== "moving" && hrs != null && !isSlaApproved && (
                        <span className={`mono text-[10px] ${mv === "no_movement" ? "text-rose-700" : "text-amber-700"}`}>
                          · last move {hrs}h ago
                        </span>
                      )}
                      {isSlaApproved && (
                        <span className="text-[10px] mono text-emerald-700">
                          · you approved ship-by {new Date(slaExt.committed_ship_by).toLocaleDateString()}
                        </span>
                      )}
                      {isSlaPending && (
                        <span className="text-[10px] mono text-amber-800 font-medium">
                          · {slaExt.requested_by_name || "Seller"} requested extension — {slaExt.reason_detail}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="truncate">{o.seller_name}</div>
                  <div className="text-right">{o.qty}</div>
                  <div className="text-right font-semibold">{money(total)}</div>
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    {/* File-claim button — appears on shipped/delivered/no-movement/stalled orders.
                        Hidden during awaiting-payment + SLA workflows because those have
                        their own resolution paths. */}
                    {!awaitingPay && !isSlaPending && (
                      ["shipped", "delivered", "in_transit"].includes(o.status) ||
                      mv === "no_movement" || mv === "stalled"
                    ) && (
                      <button onClick={() => setFileClaimFor(o)}
                        data-testid={`file-claim-${o.code}`}
                        title="File a claim against this order"
                        className="inline-flex items-center gap-1 rounded-full h-7 px-2.5 text-[10.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary text-foreground/65 hover:text-foreground">
                        <AlertTriangle className="w-3 h-3"/>Claim
                      </button>
                    )}
                    {isSlaPending ? (
                      <button onClick={() => setSlaReviewOpen(o)}
                        data-testid={`sla-review-${o.code}`}
                        className="inline-flex items-center gap-1 rounded-full h-8 px-3 text-[11px] mono uppercase tracking-[0.14em] border border-amber-300 bg-amber-400 text-amber-950 hover:bg-amber-500">
                        <Clock className="w-3.5 h-3.5"/>Review request
                      </button>
                    ) : isSlaApproved ? (
                      <span className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Check className="w-3 h-3"/>SLA extended
                      </span>
                    ) : awaitingPay ? (
                      o.awaiting_payment_reason === "ach_failed" ? (
                        <a href="/buyer/payments/methods" data-testid={`ach-review-${o.code}`}
                          title={o.awaiting_payment_error || "ACH charge failed"}
                          className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3"/>Review ACH method
                        </a>
                      ) : (
                        <a href="/payments/wallet" data-testid={`top-up-${o.code}`}
                          className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50">
                          Top up wallet
                        </a>
                      )
                    ) : mv === "no_movement" ? (
                      <span data-testid={`movement-${o.code}`}
                        className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-rose-100 text-rose-700 border-rose-200">
                        <AlertTriangle className="w-3 h-3"/>No movement
                      </span>
                    ) : mv === "stalled" ? (
                      <span data-testid={`movement-${o.code}`}
                        className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-amber-100 text-amber-700 border-amber-200">
                        <Clock className="w-3 h-3"/>Stalled
                      </span>
                    ) : mv === "moving" ? (
                      <span className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-indigo-50 text-indigo-700 border-indigo-200">
                        {o.status || "in_transit"}
                      </span>
                    ) : (
                      <span className={`text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${o.status === "delivered" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-secondary text-foreground/55 border-border"}`}>
                        {o.status || "pending"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* File-claim dialog · damaged claims require 1-3 photo proofs */}
      <FileClaimDialog open={!!fileClaimFor} order={fileClaimFor}
        onClose={() => setFileClaimFor(null)}
        onSuccess={() => onRescan?.()}/>

      {/* ─── SLA Extension · buyer-side Review dialog (PREVIEW mock) ─── */}
      <Dialog open={!!slaReviewOpen} onOpenChange={v => !v && setSlaReviewOpen(null)}>        <DialogContent className="max-w-lg">
          {slaReviewOpen && slaReviewOpen.sla_extension && (() => {
            const x = slaReviewOpen.sla_extension;
            const reasonLabel = {
              stock_delay: "Stock / restock delay",
              carrier_issue: "Carrier disruption",
              packaging_delay: "Packaging / QC delay",
              weather: "Weather · force majeure",
              other: "Other",
            }[x.reason_code] || x.reason_code;
            return (
              <>
                <DialogHeader>
                  <DialogTitle>SLA extension request — {slaReviewOpen.code}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-foreground/60 -mt-1">
                  <span className="font-medium text-foreground/85">{x.requested_by_name}</span> is asking you to move the ship-by deadline.
                  If you approve, the order counts as on-time and you waive your right to file a late-delivery claim on it.
                </p>
                <div className="mt-4 rounded-xl border border-border bg-secondary/30 p-4 space-y-3 text-[12.5px]">
                  <div>
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Reason</div>
                    <div className="mt-0.5">{reasonLabel}</div>
                  </div>
                  <div>
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Seller's note</div>
                    <div className="mt-0.5 text-foreground/85 italic">"{x.reason_detail}"</div>
                  </div>
                  <div>
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">New committed ship-by</div>
                    <div className="mt-0.5 font-medium">{new Date(x.committed_ship_by).toLocaleDateString(undefined, {weekday: "short", month: "short", day: "numeric"})}</div>
                  </div>
                  <div>
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Request expires</div>
                    <div className="mt-0.5 text-foreground/70">{Math.max(0, Math.round((new Date(x.expires_at) - Date.now()) / 3600000))}h remaining</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Button onClick={() => rejectSla(slaReviewOpen)} variant="outline"
                    data-testid="sla-reject"
                    className="rounded-full h-11 border-rose-200 text-rose-700 hover:bg-rose-50">
                    Reject
                  </Button>
                  <Button onClick={() => approveSla(slaReviewOpen)} data-testid="sla-approve"
                    className="rounded-full h-11 bg-emerald-600 text-white hover:bg-emerald-700">
                    <Check className="w-4 h-4 mr-1"/>Approve extension
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      <CsvUploadDialog
        open={csvOpen}
        onClose={() => { setCsvOpen(false); setReuploadSellerId(null); }}
        onSuccess={onCsvUploadSuccess}
        sellers={suppliers || []}
        initialSellerId={reuploadSellerId}
      />
      <CsvHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onFilterBatch={(id) => { setBatchFilter(id); setHistoryOpen(false); }}
        currentBatchFilter={batchFilter}
        onReupload={(b) => {
          // Open upload dialog with supplier pre-selected; user picks a fresh
          // CSV. If they include the same `order_id` column values as the
          // original upload, the backend overrides existing orders instead
          // of creating duplicates.
          setReuploadSellerId(b.seller_id);
          setHistoryOpen(false);
          setCsvOpen(true);
        }}
      />
    </div>
  );
}
// sellers. Actions: Withdraw (own pending), Accept seller's contest,
// Push back on seller's contest. All honor the 30-day auto-resolver.
// ─────────────────────────────────────────────────────────────────────
// ─── Claims | Returns toggle wrapper for the Claims tab ──────────────
function ClaimsOrReturns({ claims, orders, reload }) {
  const [search, setSearch] = useSearchParams();
  const view = search.get("view") === "returns" ? "returns" : "claims";
  const setView = (v) => {
    const next = new URLSearchParams(search);
    next.set("tab", "claims");
    if (v === "returns") next.set("view", "returns"); else next.delete("view");
    setSearch(next, { replace: true });
  };
  return (
    <div className="mt-10 space-y-5" data-testid="claims-or-returns-wrapper">
      <div className="flex items-center gap-1.5" data-testid="claims-returns-toggle">
        <button
          onClick={() => setView("claims")}
          data-testid="toggle-claims"
          className={`text-[12.5px] mono uppercase tracking-[0.16em] px-4 py-1.5 rounded-full border transition-all ${view === "claims" ? "bg-foreground text-background border-transparent" : "bg-white border-border text-foreground/60 hover:border-foreground/40"}`}
        >Claims</button>
        <button
          onClick={() => setView("returns")}
          data-testid="toggle-returns"
          className={`text-[12.5px] mono uppercase tracking-[0.16em] px-4 py-1.5 rounded-full border transition-all ${view === "returns" ? "bg-foreground text-background border-transparent" : "bg-white border-border text-foreground/60 hover:border-foreground/40"}`}
        >Returns</button>
      </div>
      {view === "claims"
        ? <BuyerClaimsPanel claims={claims} orders={orders} reload={reload}/>
        : <DropshipReturnsPanel role="buyer" orders={orders} reload={reload}/>}
    </div>
  );
}


function BuyerClaimsPanel({ claims, orders, reload }) {
  const [confirm, setConfirm] = useState(null); // { claim, action } OR { bulk, action, ids, rows }
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [statusChip, setStatusChip] = useState("all");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  // File-claim flow — opens an order picker first, then the FileClaimDialog.
  const [pickOrderOpen, setPickOrderOpen] = useState(false);
  const [fileClaimFor, setFileClaimFor] = useState(null);

  const normStatus = (s) => s === "disputed" ? "contested" : s;

  const toggleOne = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const submitAction = async () => {
    if (!confirm || confirm.bulk) return;
    setSubmitting(true);
    try {
      await api.post(`/dropship/claims/${confirm.claim.id}/buyer-action`, {
        action: confirm.action, note: note.trim() || null,
      });
      const msg = {
        withdraw: "Claim withdrawn — no deduction will apply.",
        accept_contest: "Accepted the seller's position — claim closed.",
        push_back: "Pushed back — seller has 30 days to respond or the deduction auto-applies.",
      }[confirm.action];
      toast.success(msg);
      setConfirm(null); setNote("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  const submitBulk = async () => {
    if (!confirm || !confirm.bulk) return;
    setSubmitting(true);
    try {
      const r = await api.post("/dropship/claims/bulk-buyer-action", {
        claim_ids: Array.from(confirm.ids),
        action: confirm.action,
        note: note.trim() || null,
      });
      const { succeeded, failed } = r.data;
      if (failed.length === 0) toast.success(`${succeeded} claim${succeeded === 1 ? "" : "s"} ${confirm.action === "withdraw" ? "withdrawn" : confirm.action === "accept_contest" ? "accepted" : "pushed back"}.`);
      else toast.warning(`${succeeded} actioned, ${failed.length} failed.`);
      setConfirm(null); setNote(""); clearSelection();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    } finally { setSubmitting(false); }
  };

  const kindOptions = useMemo(() => {
    const seen = new Set();
    claims.forEach(c => { if (c.kind) seen.add(c.kind); });
    return [...seen].map(v => ({ value: v, label: v.replace("_", " ") }));
  }, [claims]);
  const supplierOptions = useMemo(() => {
    const seen = new Set();
    claims.forEach(c => { if (c.seller_name) seen.add(c.seller_name); });
    return [...seen].map(v => ({ value: v, label: v }));
  }, [claims]);

  const filtered = useMemo(() => {
    let xs = claims;
    if (statusChip !== "all") xs = xs.filter(c => normStatus(c.status) === statusChip);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(c =>
        (c.order_code || "").toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q) ||
        (c.kind || "").toLowerCase().includes(q) ||
        (c.seller_name || "").toLowerCase().includes(q)
      );
    }
    const F = filters || {};
    if (F.kind?.values?.length)     xs = xs.filter(c => F.kind.values.includes(c.kind));
    if (F.status?.values?.length)   xs = xs.filter(c => F.status.values.includes(normStatus(c.status)));
    if (F.supplier?.values?.length) xs = xs.filter(c => F.supplier.values.includes(c.seller_name));
    if (F.deduction?.value != null) {
      xs = xs.filter(c => {
        const v = Number(c.deduction) || 0;
        if (F.deduction.op === "gt") return v > Number(F.deduction.value);
        if (F.deduction.op === "lt") return v < Number(F.deduction.value);
        if (F.deduction.op === "between") return v >= Number(F.deduction.min) && v <= Number(F.deduction.max);
        return true;
      });
    }
    if (F.date?.value) xs = xs.filter(c => (c.created_at || "") >= F.date.value);
    return xs;
  }, [claims, statusChip, search, filters]);

  const chipCount = (s) => claims.filter(c => normStatus(c.status) === s).length;

  const summary = useMemo(() => {
    const pending = claims.filter(c => c.status === "pending");
    const contested = claims.filter(c => normStatus(c.status) === "contested");
    const pendingOnInvoice = pending.reduce((a, c) => a + (Number(c.deduction) || 0), 0);
    const accepted = claims.filter(c => c.status === "accepted");
    const acceptedPending = accepted.reduce((a, c) => a + (Number(c.deduction) || 0), 0);
    return { pendingOnInvoice, contestedCount: contested.length, acceptedPending };
  }, [claims]);

  const daysUntilDeadline = (iso) => {
    if (!iso) return null;
    const d = new Date(iso).getTime() - Date.now();
    if (Number.isNaN(d)) return null;
    return Math.max(0, Math.ceil(d / 86400000));
  };

  return (
    <div className="mt-10 space-y-6" data-testid="buyer-claims-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Claims against suppliers</div>
          <h3 className="display text-3xl mt-2 tracking-tight">Your filed claims</h3>
          <p className="text-sm text-foreground/60 max-w-2xl mt-2">
            Claims you've filed auto-deduct from your next daily invoice unless the seller contests them.
            You can withdraw your own claim, accept the seller's position, or push back — after push-back,
            if the seller doesn't respond within 30 days, the deduction auto-applies on the next invoice
            as a prior-period adjustment.
          </p>
        </div>
        <Button onClick={() => setPickOrderOpen(true)}
          data-testid="file-new-claim-btn"
          className="rounded-full h-10 px-5 bg-foreground text-background hover:bg-foreground/90 shrink-0">
          <Plus className="w-4 h-4 mr-1.5"/>File a claim
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3">
        <KpiTile label="Pending auto-deduction" value={money(summary.pendingOnInvoice)} hint="Applies to next daily invoice unless the seller contests"/>
        <KpiTile label="Under dispute" value={summary.contestedCount} hint="Contested claims awaiting resolution"/>
        <KpiTile label="Accepted · prior-period" value={money(summary.acceptedPending)} hint="Auto-accepted or seller-agreed; rolls onto next invoice"/>
      </div>

      {/* Status chips */}
      <div className="flex gap-1.5 flex-wrap" data-testid="buyer-claims-chips">
        {[
          { k: "all",       l: "All",       c: claims.length },
          { k: "pending",   l: "Pending",   c: chipCount("pending") },
          { k: "contested", l: "Under dispute", c: chipCount("contested") },
          { k: "accepted",  l: "Accepted",  c: chipCount("accepted") },
          { k: "waived",    l: "Closed",    c: chipCount("waived") },
          { k: "applied",   l: "Applied",   c: chipCount("applied") },
        ].map(f => (
          <button key={f.k} onClick={() => setStatusChip(f.k)} data-testid={`buyer-claims-chip-${f.k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[12.5px] border transition-colors ${statusChip === f.k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>
            {f.l}
            <span className={`text-[10.5px] mono px-1.5 rounded-full ${statusChip === f.k ? "bg-background/20" : "bg-secondary"}`}>{f.c}</span>
          </button>
        ))}
      </div>

      {/* Power-user facet bar */}
      <SearchAndFilter
        page="buyer_dropship_claims"
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onChange={setFilters}
        totalCount={filtered.length}
        facets={[
          { id: "kind",      label: "Kind",      icon: AlertTriangle, type: "multi", options: kindOptions },
          { id: "status",    label: "Status",    icon: Activity,      type: "multi", options: [
            { value: "pending",   label: "Pending" },
            { value: "contested", label: "Under dispute" },
            { value: "accepted",  label: "Accepted" },
            { value: "waived",    label: "Closed" },
            { value: "applied",   label: "Applied" },
          ]},
          { id: "supplier",  label: "Supplier",  icon: Users,         type: "multi", options: supplierOptions },
          { id: "deduction", label: "Deduction", icon: DollarSign,    type: "numeric", numericLabel: "$" },
          { id: "date",      label: "Filed after", icon: Calendar,    type: "date" },
        ]}
      />

      {/* Bulk action bar — shows when ≥1 row selected */}
      {selected.size > 0 && (() => {
        const selectedRows = claims.filter(c => selected.has(c.id));
        const allPending   = selectedRows.every(c => c.status === "pending");
        const allContested = selectedRows.every(c => normStatus(c.status) === "contested");
        const canWithdraw  = allPending;
        const canAccept    = allContested;
        const canPushBack  = allContested;
        const hint = !allPending && !allContested
          ? "Mixed selection — pick rows with the same status to act in bulk."
          : null;
        return (
          <div className="sticky top-3 z-20 rounded-2xl border border-foreground/20 bg-foreground text-background shadow-lg px-5 py-3 flex items-center gap-4 flex-wrap" data-testid="buyer-claims-bulk-bar">
            <div className="text-[13px] font-medium">{selected.size} selected</div>
            {hint && <div className="text-[11.5px] text-background/70 hidden md:block">{hint}</div>}
            <div className="ml-auto flex items-center gap-2">
              <button disabled={!canWithdraw}
                onClick={() => { setConfirm({ bulk: true, action: "withdraw", ids: new Set(selected), rows: selectedRows }); setNote(""); }}
                data-testid="buyer-bulk-withdraw"
                className={`rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] ${canWithdraw ? "bg-background/10 text-background border border-background/30 hover:bg-background/20" : "bg-white/5 text-background/40 cursor-not-allowed"}`}>
                Withdraw
              </button>
              <button disabled={!canAccept}
                onClick={() => { setConfirm({ bulk: true, action: "accept_contest", ids: new Set(selected), rows: selectedRows }); setNote(""); }}
                data-testid="buyer-bulk-accept"
                className={`rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] ${canAccept ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-white/5 text-background/40 cursor-not-allowed"}`}>
                Accept
              </button>
              <button disabled={!canPushBack}
                onClick={() => { setConfirm({ bulk: true, action: "push_back", ids: new Set(selected), rows: selectedRows }); setNote(""); }}
                data-testid="buyer-bulk-push-back"
                className={`rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] ${canPushBack ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-white/5 text-background/40 cursor-not-allowed"}`}>
                Push back
              </button>
              <button onClick={clearSelection} data-testid="buyer-bulk-clear"
                className="text-[11.5px] mono uppercase tracking-[0.14em] text-background/70 hover:text-background ml-2">
                Clear
              </button>
            </div>
          </div>
        );
      })()}

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/20 px-6 py-12 text-center">
          <ShieldCheck className="w-8 h-8 mx-auto text-foreground/30 mb-2"/>
          <div className="text-sm font-medium">{claims.length === 0 ? "No claims filed" : "No claims match these filters"}</div>
          <div className="text-xs text-foreground/55 mt-1">{claims.length === 0 ? "File a claim from the order drawer when a supplier misses an SLA or ships the wrong item." : "Clear a filter or chip above to see more claims."}</div>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          {(() => {
            const visibleSelectable = filtered.filter(c => {
              const s = normStatus(c.status);
              return s === "pending" || s === "contested";
            });
            const allVisibleSelected = visibleSelectable.length > 0 && visibleSelectable.every(c => selected.has(c.id));
            const someVisibleSelected = visibleSelectable.some(c => selected.has(c.id));
            const toggleAll = () => setSelected(s => {
              const n = new Set(s);
              if (allVisibleSelected) visibleSelectable.forEach(c => n.delete(c.id));
              else visibleSelectable.forEach(c => n.add(c.id));
              return n;
            });
            return (
              <div className="grid grid-cols-[36px_110px_130px_1fr_100px_140px_200px] gap-3 px-5 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
                <div className="flex items-center justify-center">
                  <input type="checkbox"
                    data-testid="buyer-bulk-select-all"
                    disabled={visibleSelectable.length === 0}
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleAll}
                    className="h-3.5 w-3.5 rounded border-foreground/30 cursor-pointer disabled:cursor-not-allowed"/>
                </div>
                <div>Order</div><div>Kind</div><div>Description</div><div className="text-right">Deduction</div><div>Status</div><div className="text-right">Action</div>
              </div>
            );
          })()}
          <div className="divide-y divide-border">
            {filtered.map(c => {
              const s = normStatus(c.status);
              const contestedBySeller = s === "contested" && (c.contested_by === "seller" || c.last_action_by === "seller");
              const pushedBackByMe   = s === "contested" && (c.last_action_by === "buyer");
              const days = daysUntilDeadline(c.resolution_deadline);
              const rowSelectable = s === "pending" || s === "contested";
              const rowSelected = selected.has(c.id);
              return (
                <div key={c.id} className={`grid grid-cols-[36px_110px_130px_1fr_100px_140px_200px] gap-3 px-5 py-3 items-center text-[13px] ${rowSelected ? "bg-foreground/[0.04]" : ""}`} data-testid={`buyer-claim-${c.id}`}>
                  <div className="flex items-center justify-center">
                    <input type="checkbox"
                      data-testid={`buyer-row-check-${c.id}`}
                      disabled={!rowSelectable}
                      checked={rowSelected}
                      onChange={() => toggleOne(c.id)}
                      className="h-3.5 w-3.5 rounded border-foreground/30 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"/>
                  </div>
                  <div className="mono text-[12px]">{c.order_code}</div>
                  <div className="capitalize">
                    {(c.kind || "").replace("_", " ")}
                    {c.carrier_claim && (
                      <CarrierClaimPill cc={c.carrier_claim}/>
                    )}
                  </div>
                  <div className="truncate text-foreground/70">{c.description}</div>
                  <div className="text-right font-semibold">
                    {s === "waived"   ? <span className="text-emerald-700">Closed</span>
                      : s === "accepted" ? <span className="text-indigo-700">{money(c.deduction)}</span>
                      : <span className="text-rose-700">− {money(c.deduction)}</span>}
                  </div>
                  <div>
                    <span className={`text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${
                      s === "pending"   ? "bg-amber-50 text-amber-700 border-amber-200" :
                      s === "waived"    ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      s === "contested" ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                      s === "accepted"  ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                      s === "applied"   ? "bg-rose-50 text-rose-700 border-rose-200" :
                      "bg-secondary text-foreground/55 border-border"
                    }`}>
                      {s === "contested" && <Clock className="w-2.5 h-2.5"/>}
                      {s}
                    </span>
                    {s === "contested" && days != null && (
                      <div className="text-[10px] text-foreground/50 mt-0.5">
                        {pushedBackByMe ? `Seller has ${days}d to reply` : `${days}d to respond`}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    {s === "pending" ? (
                      <button
                        onClick={() => { setConfirm({ claim: c, action: "withdraw" }); setNote(""); }}
                        data-testid={`withdraw-${c.id}`}
                        title="Drop your own claim — no deduction will apply."
                        className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-border text-foreground/75 hover:bg-secondary transition-colors">
                        Withdraw
                      </button>
                    ) : contestedBySeller ? (
                      <>
                        <button
                          onClick={() => { setConfirm({ claim: c, action: "accept_contest" }); setNote(""); }}
                          data-testid={`accept-contest-${c.id}`}
                          title="Agree with the seller — closes the claim with no deduction."
                          className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors">
                          Accept
                        </button>
                        <button
                          onClick={() => { setConfirm({ claim: c, action: "push_back" }); setNote(""); }}
                          data-testid={`push-back-${c.id}`}
                          title="Insist on the claim — resets the 30-day timer; if the seller stays silent, deduction auto-applies on the next invoice."
                          className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-border bg-foreground text-background hover:bg-foreground/90 transition-colors">
                          Push back
                        </button>
                      </>
                    ) : pushedBackByMe ? (
                      <span className="text-[10.5px] mono text-foreground/50 inline-flex items-center gap-1">
                        <MessageSquare className="w-3 h-3"/>Awaiting seller
                      </span>
                    ) : (
                      <span className="text-[10.5px] mono text-foreground/40">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onOpenChange={(v) => { if (!v) { setConfirm(null); setNote(""); } }}>
        <DialogContent className="max-w-md" data-testid="buyer-claim-action-dialog">
          {confirm && (() => {
            const isBulk = !!confirm.bulk;
            const count  = isBulk ? confirm.ids.size : 1;
            const totalDeduction = isBulk
              ? (confirm.rows || []).reduce((a, c) => a + (Number(c.deduction) || 0), 0)
              : Number(confirm.claim?.deduction || 0);
            const titleSingular = { withdraw: "Withdraw this claim?", accept_contest: "Accept the seller's contest?", push_back: "Push back on this claim?" }[confirm.action];
            const titleBulk     = { withdraw: `Withdraw ${count} claims?`, accept_contest: `Accept ${count} contests?`, push_back: `Push back on ${count} claims?` }[confirm.action];
            return (
            <>
              <DialogHeader>
                <DialogTitle>{isBulk ? titleBulk : titleSingular}</DialogTitle>
              </DialogHeader>

              <div className="rounded-xl bg-secondary/40 border border-border p-3 text-[13px] space-y-1">
                {isBulk ? (
                  <>
                    <div><span className="text-foreground/55">Claims selected:</span> <span className="font-semibold">{count}</span></div>
                    <div><span className="text-foreground/55">Combined deduction at stake:</span> <span className="font-semibold">{money(totalDeduction)}</span></div>
                  </>
                ) : (
                  <>
                    <div><span className="text-foreground/55">Order:</span> <span className="mono">{confirm.claim.order_code}</span></div>
                    <div><span className="text-foreground/55">Kind:</span> <span className="capitalize">{(confirm.claim.kind || "").replace("_", " ")}</span></div>
                    <div><span className="text-foreground/55">Deduction at stake:</span> <span className="font-semibold">{money(confirm.claim.deduction)}</span></div>
                  </>
                )}
              </div>

              {/* Evidence photos — only on single-claim confirms */}
              {!isBulk && (confirm.claim.evidence_photo_count || 0) > 0 && (
                <ClaimEvidenceGallery claim={confirm.claim}/>
              )}

              {/* Iter 238 · Live Verqo Shipping (EasyPost) claim status */}
              {!isBulk && confirm.claim?.easypost_claim_id && (
                <VerqoShippingClaimTimeline claimId={confirm.claim.id} role="buyer"/>
              )}

              <div className={`rounded-xl border p-3 text-[12.5px] ${
                confirm.action === "push_back" ? "bg-amber-50 border-amber-200" :
                confirm.action === "accept_contest" ? "bg-emerald-50 border-emerald-200" :
                "bg-secondary border-border"}`}>
                {confirm.action === "withdraw" && (
                  <>
                    <div className="font-semibold">{isBulk ? `${count} claims will close with no deduction.` : "Your claim will be closed with no deduction."}</div>
                    <div className="text-foreground/70 mt-0.5">The seller's invoice will not be reduced. You can file a fresh claim later if the situation changes.</div>
                  </>
                )}
                {confirm.action === "accept_contest" && (
                  <>
                    <div className="font-semibold text-emerald-800">You'll agree with the seller{isBulk ? ` on ${count} claims` : ""} and close.</div>
                    <div className="text-emerald-900/75 mt-0.5">No deduction will be applied. Logged to both profiles as amicably-resolved.</div>
                  </>
                )}
                {confirm.action === "push_back" && (
                  <>
                    <div className="font-semibold text-amber-800">You'll insist on the claim{isBulk ? `s` : ""}. 30-day timer starts{isBulk ? ` on each` : ""}.</div>
                    <div className="text-amber-900/75 mt-0.5">
                      If the seller doesn't respond within 30 days, the deduction auto-applies on
                      the next invoice as a "prior-period adjustment". Reach out in Messages to resolve quickly.
                    </div>
                  </>
                )}
              </div>

              <div>
                <Label className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">{isBulk ? "Note applied to all (optional)" : "Note (optional)"}</Label>
                <Input value={note} onChange={e => setNote(e.target.value)}
                  placeholder={confirm.action === "push_back"
                    ? "e.g. Photo + tracking screenshot emailed"
                    : "e.g. Resolved directly with seller"}
                  className="mt-1 h-10" data-testid="buyer-claim-action-note"/>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setConfirm(null); setNote(""); }} className="rounded-full h-10 px-4">Cancel</Button>
                <Button onClick={isBulk ? submitBulk : submitAction} disabled={submitting}
                  data-testid="buyer-claim-action-confirm"
                  className="rounded-full h-10 px-4 bg-foreground text-background hover:bg-foreground/90">
                  {submitting ? "…" : isBulk ? `Confirm ${count}` : "Confirm"}
                </Button>
              </div>
            </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Order picker — step 1 of "File a claim" from the Claims tab */}
      <Dialog open={pickOrderOpen} onOpenChange={(v) => !v && setPickOrderOpen(false)}>
        <DialogContent className="max-w-xl" data-testid="claim-order-picker">
          <DialogHeader>
            <DialogTitle>Pick an order to file a claim against</DialogTitle>
            <DialogDescription>
              We list shipped, in-transit, delivered, and stalled orders — claims on still-processing orders need to wait for SLA resolution first.
            </DialogDescription>
          </DialogHeader>
          <ClaimOrderPicker
            orders={orders}
            onPick={(o) => { setPickOrderOpen(false); setFileClaimFor(o); }}
          />
        </DialogContent>
      </Dialog>

      {/* Step 2 — same FileClaimDialog used by the Daily Orders pill */}
      <FileClaimDialog
        open={!!fileClaimFor}
        order={fileClaimFor}
        onClose={() => setFileClaimFor(null)}
        onSuccess={() => reload?.()}
      />
    </div>
  );
}

// Tiny shared picker used by the Claims tab's "File a claim" CTA. Only
// surfaces orders that are claim-eligible (shipped / delivered / in-transit
// / stalled / no-movement). Click a row → returns it to the caller.
function ClaimOrderPicker({ orders, onPick }) {
  const [q, setQ] = useState("");
  const eligible = useMemo(() => {
    return (orders || []).filter(o =>
      ["shipped", "delivered", "in_transit"].includes(o.status) ||
      o.movement_status === "no_movement" ||
      o.movement_status === "stalled"
    );
  }, [orders]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return eligible;
    return eligible.filter(o =>
      (o.code || "").toLowerCase().includes(t) ||
      (o.customer_name || "").toLowerCase().includes(t) ||
      (o.product_name || "").toLowerCase().includes(t) ||
      (o.seller_name || "").toLowerCase().includes(t),
    );
  }, [eligible, q]);
  return (
    <div className="space-y-3 mt-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search by order code · customer · product · supplier"
        data-testid="claim-picker-search" autoFocus/>
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-secondary/20 px-5 py-10 text-center text-[13px] text-foreground/55">
          {eligible.length === 0
            ? "No claim-eligible orders yet. Once an order ships you'll be able to file a claim."
            : "No orders match your search."}
        </div>
      ) : (
        <div className="rounded-xl border border-border max-h-[420px] overflow-y-auto divide-y divide-border">
          {filtered.slice(0, 50).map(o => (
            <button key={o.id} onClick={() => onPick(o)}
              data-testid={`claim-picker-row-${o.code}`}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/40 text-left">
              <div className="mono text-[12px] w-20 shrink-0">{o.code}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate">{o.customer_name}</div>
                <div className="text-[11px] text-foreground/55 truncate">{o.product_name} · {o.seller_name}</div>
              </div>
              <span className="text-[10px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border border-border bg-white text-foreground/70 capitalize shrink-0">
                {(o.status || "").replace("_", " ")}
              </span>
            </button>
          ))}
          {filtered.length > 50 && (
            <div className="px-4 py-2 text-[11px] text-foreground/45 text-center bg-secondary/20">
              Showing top 50 — narrow with search.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">{label}</div>
      <div className="display text-2xl mt-1.5 tracking-tight">{value}</div>
      {hint && <div className="text-[11px] text-foreground/55 mt-1.5 leading-snug">{hint}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DropshipScheduleEditor — per-store cutoff + invoice-generation time.
// Each row is saved independently via PATCH /dropship/stores/{sid}/schedule.
// Dirty rows get a Save button; the rest show "Schedule saved · X" hint.
// ─────────────────────────────────────────────────────────────────────
const DROPSHIP_TIMEZONES = [
  { v: "America/New_York",    l: "ET · New York" },
  { v: "America/Chicago",     l: "CT · Chicago" },
  { v: "America/Denver",      l: "MT · Denver" },
  { v: "America/Los_Angeles", l: "PT · Los Angeles" },
  { v: "America/Phoenix",     l: "MST · Phoenix (no DST)" },
  { v: "Europe/London",       l: "GMT/BST · London" },
  { v: "Europe/Paris",        l: "CET · Paris" },
  { v: "Asia/Singapore",      l: "SGT · Singapore" },
  { v: "Asia/Tokyo",          l: "JST · Tokyo" },
  { v: "Australia/Sydney",    l: "AEST · Sydney" },
  { v: "UTC",                 l: "UTC" },
];

function _fmt12(t) {
  if (!t || !t.includes(":")) return t;
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

function DropshipScheduleEditor({ store, onSaved }) {
  // Per-storefront schedule editor — Buyer can have many storefronts; this
  // editor binds to ONE at a time (the parent decides which).
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!store) return;
    setDraft({
      timezone:               store.timezone || "America/New_York",
      confirm_cutoff_time:    store.confirm_cutoff_time || "11:00",
      invoice_generation_time: store.invoice_generation_time || "23:30",
    });
  }, [store?.id, store?.timezone, store?.confirm_cutoff_time, store?.invoice_generation_time]);

  if (!store || !draft) return null;

  const dirty = (
    draft.timezone !== (store.timezone || "America/New_York") ||
    draft.confirm_cutoff_time !== (store.confirm_cutoff_time || "11:00") ||
    draft.invoice_generation_time !== (store.invoice_generation_time || "23:30")
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/dropship/stores/${store.id}/schedule`, draft);
      toast.success("Schedule saved");
      onSaved?.();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(Array.isArray(msg) ? msg[0]?.msg || "Failed" : (msg || "Failed"));
    } finally { setSaving(false); }
  };

  const reset = () => setDraft({
    timezone:               store.timezone || "America/New_York",
    confirm_cutoff_time:    store.confirm_cutoff_time || "11:00",
    invoice_generation_time: store.invoice_generation_time || "23:30",
  });

  return (
    <div className="rounded-2xl border border-border bg-white p-7" data-testid="dropship-schedule-editor">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-4 h-4 text-foreground/60"/>
        <div className="display text-2xl">Daily schedule</div>
        {!dirty && (
          <span className="ml-auto text-[11px] mono uppercase tracking-[0.16em] text-foreground/50 flex items-center gap-1">
            <Check className="w-3 h-3"/> Saved
          </span>
        )}
      </div>
      <p className="text-sm text-foreground/65 max-w-2xl">
        Cutoff time (by when you must confirm today's orders) and the invoice-generation time.
        Both are interpreted in your store's timezone.
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">Timezone</Label>
          <select
            value={draft.timezone}
            onChange={e => setDraft({...draft, timezone: e.target.value})}
            data-testid="schedule-tz"
            className="mt-1 w-full h-10 rounded-md border border-border bg-white px-3 text-[13px]">
            {DROPSHIP_TIMEZONES.map(tz => (
              <option key={tz.v} value={tz.v}>{tz.l}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">Confirm cutoff</Label>
          <Input type="time" value={draft.confirm_cutoff_time}
            onChange={e => setDraft({...draft, confirm_cutoff_time: e.target.value})}
            data-testid="schedule-cutoff"
            className="mt-1 h-10 mono"/>
          <div className="text-[10.5px] text-foreground/50 mt-1">
            {_fmt12(draft.confirm_cutoff_time)} in {draft.timezone.split("/").pop().replace("_", " ")}
          </div>
        </div>
        <div>
          <Label className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">Invoice runs at</Label>
          <Input type="time" value={draft.invoice_generation_time}
            onChange={e => setDraft({...draft, invoice_generation_time: e.target.value})}
            data-testid="schedule-invoice"
            className="mt-1 h-10 mono"/>
          <div className="text-[10.5px] text-foreground/50 mt-1">
            {_fmt12(draft.invoice_generation_time)} in {draft.timezone.split("/").pop().replace("_", " ")}
          </div>
        </div>
      </div>
      {dirty && (
        <div className="mt-4 flex gap-2 justify-end">
          <Button variant="outline" onClick={reset} disabled={saving}
                  className="rounded-full h-9 px-4 text-[12.5px]"
                  data-testid="schedule-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving}
                  className="rounded-full h-9 px-4 text-[12.5px] bg-foreground text-background hover:bg-foreground/90"
                  data-testid="schedule-save">
            {saving ? "Saving…" : "Save schedule"}
          </Button>
        </div>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────
// BuyerInvoicesPanel — buyer's dropship invoices with PDF/CSV download.
// ─────────────────────────────────────────────────────────────────────
// InvoiceAutopayChip + PrepayButton — shown in the buyer invoice row
// when the pair has a payment_policy configured. Both are no-ops for
// policies that don't schedule autopay (manual / prepaid / absent).
// ─────────────────────────────────────────────────────────────────────
function InvoiceAutopayChip({ inv }) {
  const s = inv.autopay_status;
  if (!s || s === "manual") return null;
  const tone =
    s === "scheduled"  ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
    s === "succeeded"  ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    s === "failed"     ? "bg-rose-50 text-rose-700 border-rose-200" :
    s === "prepaid"    ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          "bg-foreground/5 text-foreground/60 border-border";
  const label =
    s === "scheduled" ? "Autopay scheduled"
    : s === "succeeded" ? "Autopaid"
    : s === "failed"   ? "Autopay failed"
    : s === "prepaid"  ? "Prepaid"
    : s;
  const when = s === "scheduled" && inv.autopay_scheduled_at
    ? ` · ${new Date(inv.autopay_scheduled_at).toLocaleDateString()}`
    : "";
  return (
    <span title={label + when} data-testid={`autopay-chip-${inv.code}`}
          className={`text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${tone}`}>
      <CalendarClock className="w-3 h-3"/>{label}{when}
    </span>
  );
}

function PrepayButton({ inv, onPaid }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (!window.confirm(`Prepay ${money(inv.total)} now via ACH?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/dropship/invoices/${inv.id}/prepay`);
      if (r.data?.already_paid) {
        toast.info("Invoice was already paid");
      } else {
        toast.success(`Invoice paid${r.data?.sola_ref_num ? ` — ref ${r.data.sola_ref_num}` : ""}`);
      }
      onPaid && onPaid();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Prepay failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={onClick} disabled={busy} title="Prepay via ACH now"
            data-testid={`prepay-${inv.code}`}
            className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-foreground bg-foreground text-background hover:bg-foreground/90 flex items-center gap-1 disabled:opacity-50">
      <Sparkles className="w-3 h-3"/>{busy ? "…" : "Prepay"}
    </button>
  );
}


// ─────────────────────────────────────────────────────────────────────
function BuyerInvoicesPanel() {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [statusChip, setStatusChip] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    api.get("/dropship/invoices").then(r => setInvoices(r.data || [])).catch(() => {});
  }, []);

  const toggleOne = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const supplierOptions = useMemo(() => {
    const seen = new Set();
    invoices.forEach(i => { if (i.seller_name) seen.add(i.seller_name); });
    return [...seen].map(v => ({ value: v, label: v }));
  }, [invoices]);

  const filtered = useMemo(() => {
    let xs = invoices;
    if (statusChip !== "all") xs = xs.filter(i => (i.status || "issued") === statusChip);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(i =>
        (i.code || "").toLowerCase().includes(q) ||
        (i.seller_name || "").toLowerCase().includes(q)
      );
    }
    const F = filters || {};
    if (F.supplier?.values?.length) xs = xs.filter(i => F.supplier.values.includes(i.seller_name));
    if (F.status?.values?.length)   xs = xs.filter(i => F.status.values.includes(i.status || "issued"));
    if (F.hasAdjustments?.on != null) xs = xs.filter(i => F.hasAdjustments.on ? (i.prior_adjustments_count || 0) > 0 : (i.prior_adjustments_count || 0) === 0);
    if (F.total?.value != null) {
      xs = xs.filter(i => {
        const v = Number(i.total || 0);
        if (F.total.op === "gt") return v > Number(F.total.value);
        if (F.total.op === "lt") return v < Number(F.total.value);
        if (F.total.op === "between") return v >= Number(F.total.min) && v <= Number(F.total.max);
        return true;
      });
    }
    if (F.date?.value) xs = xs.filter(i => (i.date || i.created_at || "") >= F.date.value);
    return xs;
  }, [invoices, search, filters, statusChip]);

  const summary = useMemo(() => ({
    count:      invoices.length,
    total_owed: invoices.filter(i => i.status === "issued").reduce((a, i) => a + (i.total || 0), 0),
    total_paid: invoices.filter(i => i.status === "paid").reduce((a, i) => a + (i.total || 0), 0),
    prior_adj:  invoices.reduce((a, i) => a + (i.prior_adjustments_total || 0), 0),
  }), [invoices]);

  const chipCount = (s) => invoices.filter(i => (i.status || "issued") === s).length;

  const downloadFile = async (inv, kind) => {
    try {
      const r = await api.get(`/dropship/invoices/${inv.id}/${kind}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: kind === "pdf" ? "application/pdf" : "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.code}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`Could not download ${kind.toUpperCase()}`);
    }
  };

  const bulkDownload = async (format) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/dropship/invoices/bulk-download",
        { invoice_ids: ids, format },
        { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `dropship-invoices-${today}-${ids.length}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids.length} invoice${ids.length === 1 ? "" : "s"} (${format.toUpperCase()})`);
      clearSelection();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk download failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="mt-10 space-y-6" data-testid="buyer-invoices-panel">
      <div>
        <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Daily dropship invoices</div>
        <h3 className="display text-3xl mt-2 tracking-tight">Your invoices</h3>
        <p className="text-sm text-foreground/60 max-w-2xl mt-2">
          Consolidated daily invoices from each supplier. Download PDFs for your books or CSVs for your bookkeeper.
        </p>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <KpiTile label="Invoices" value={summary.count}/>
        <KpiTile label="Currently owed" value={money(summary.total_owed)}/>
        <KpiTile label="Paid YTD" value={money(summary.total_paid)}/>
        <KpiTile label="Prior-period adjustments" value={money(summary.prior_adj)} hint="Claims accepted after previous invoices issued"/>
      </div>

      <div className="flex gap-1.5 flex-wrap" data-testid="buyer-invoices-chips">
        {[
          { k: "all",    l: "All",    c: invoices.length },
          { k: "issued", l: "Issued", c: chipCount("issued") },
          { k: "paid",   l: "Paid",   c: chipCount("paid") },
        ].map(f => (
          <button key={f.k} onClick={() => setStatusChip(f.k)} data-testid={`buyer-invoices-chip-${f.k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[12.5px] border transition-colors ${statusChip === f.k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>
            {f.l}
            <span className={`text-[10.5px] mono px-1.5 rounded-full ${statusChip === f.k ? "bg-background/20" : "bg-secondary"}`}>{f.c}</span>
          </button>
        ))}
      </div>

      <SearchAndFilter
        page="buyer_dropship_invoices"
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onChange={setFilters}
        totalCount={filtered.length}
        facets={[
          { id: "supplier",       label: "Supplier",      icon: Users,      type: "multi", options: supplierOptions },
          { id: "status",         label: "Status",        icon: Activity,   type: "multi", options: [
            { value: "issued", label: "Issued" },
            { value: "paid",   label: "Paid" },
          ]},
          { id: "hasAdjustments", label: "Has adjustments", icon: AlertTriangle, type: "boolean" },
          { id: "total",          label: "Total",         icon: DollarSign, type: "numeric", numericLabel: "$" },
          { id: "date",           label: "Issued after",  icon: Calendar,   type: "date" },
        ]}
      />

      {/* Bulk-action bar — shows when ≥1 row selected */}
      {selected.size > 0 && (
        <div className="sticky top-3 z-20 rounded-2xl border border-foreground/20 bg-foreground text-background shadow-lg px-5 py-3 flex items-center gap-4 flex-wrap" data-testid="buyer-invoices-bulk-bar">
          <div className="text-[13px] font-medium">{selected.size} selected</div>
          <div className="ml-auto flex items-center gap-2">
            <button disabled={bulkBusy} onClick={() => bulkDownload("pdf")}
              data-testid="buyer-bulk-download-pdf"
              className="rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] bg-background/10 text-background border border-background/30 hover:bg-background/20 disabled:opacity-50 inline-flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5"/>PDFs
            </button>
            <button disabled={bulkBusy} onClick={() => bulkDownload("csv")}
              data-testid="buyer-bulk-download-csv"
              className="rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] bg-background/10 text-background border border-background/30 hover:bg-background/20 disabled:opacity-50 inline-flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5"/>CSVs
            </button>
            <button disabled={bulkBusy} onClick={() => bulkDownload("both")}
              data-testid="buyer-bulk-download-both"
              className="rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] bg-background text-foreground hover:bg-background/90 disabled:opacity-50 inline-flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5"/>Both (zip)
            </button>
            <button onClick={clearSelection} data-testid="buyer-invoices-bulk-clear"
              className="text-[11.5px] mono uppercase tracking-[0.14em] text-background/70 hover:text-background ml-2">
              Clear
            </button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/20 px-6 py-12 text-center">
          <Receipt className="w-8 h-8 mx-auto text-foreground/30 mb-2"/>
          <div className="text-sm font-medium">{invoices.length === 0 ? "No invoices yet" : "No invoices match these filters"}</div>
          <div className="text-xs text-foreground/55 mt-1">{invoices.length === 0 ? "Daily consolidated invoices will appear here once your suppliers ship." : "Clear a filter or chip above."}</div>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          {(() => {
            const filteredIds = filtered.map(i => i.id);
            const allSelected = filteredIds.length > 0 && filteredIds.every(id => selected.has(id));
            const someSelected = filteredIds.some(id => selected.has(id));
            const toggleAll = () => {
              setSelected(s => {
                const n = new Set(s);
                if (allSelected) filteredIds.forEach(id => n.delete(id));
                else filteredIds.forEach(id => n.add(id));
                return n;
              });
            };
            return (
              <div className="grid grid-cols-[36px_160px_1fr_90px_120px_120px_360px] gap-3 px-5 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
                <div className="flex items-center">
                  <input type="checkbox"
                    ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                    checked={allSelected}
                    onChange={toggleAll}
                    data-testid="buyer-invoices-select-all"
                    className="w-4 h-4 cursor-pointer accent-foreground"
                  />
                </div>
                <div>Invoice</div><div>Supplier</div><div>Date</div><div className="text-right">Orders</div><div className="text-right">Total</div><div className="text-right">Actions</div>
              </div>
            );
          })()}
          <div className="divide-y divide-border">
            {filtered.map(inv => (
              <div key={inv.id} className={`grid grid-cols-[36px_160px_1fr_90px_120px_120px_360px] gap-3 px-5 py-3 items-center text-[13px] ${selected.has(inv.id) ? "bg-indigo-50/50" : ""}`} data-testid={`buyer-invoice-${inv.code}`}>
                <div className="flex items-center">
                  <input type="checkbox"
                    checked={selected.has(inv.id)}
                    onChange={() => toggleOne(inv.id)}
                    data-testid={`buyer-invoice-select-${inv.code}`}
                    className="w-4 h-4 cursor-pointer accent-foreground"
                  />
                </div>
                <div className="mono text-[12px]">{inv.code}</div>
                <div className="truncate">
                  <div className="truncate">{inv.seller_name || "—"}</div>
                  {(inv.prior_adjustments_count || 0) > 0 && (
                    <div className="text-[10.5px] text-indigo-700 mt-0.5">
                      + {inv.prior_adjustments_count} prior-period adj.
                    </div>
                  )}
                </div>
                <div className="text-foreground/65 text-[12px] mono">{(inv.date || "").slice(0, 10)}</div>
                <div className="text-right">{inv.order_count}</div>
                <div className="text-right font-semibold">
                  {money(inv.total)}
                  {inv.platform_fee_paid_by === "buyer" && inv.platform_fee_amount > 0 && (
                    <div className="text-[10px] mono text-blue-700 mt-0.5 font-normal" title={`Includes ${(Number(inv.platform_fee_pct) || 0).toFixed(1)}% verqo platform fee · ${money(inv.subtotal)} goods + ${money(inv.platform_fee_amount)} fee`}>
                      incl {money(inv.platform_fee_amount)} platform fee
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end gap-1">
                  <InvoiceAutopayChip inv={inv}/>
                  <span className={`text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${inv.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{inv.status || "issued"}</span>
                  {inv.status !== "paid" && inv.autopay_method_id && (inv.autopay_status === "scheduled" || inv.autopay_status === "manual") && (
                    <PrepayButton inv={inv} onPaid={() => api.get("/dropship/invoices").then(r => setInvoices(r.data || []))}/>
                  )}
                  <button onClick={() => downloadFile(inv, "pdf")} title="Download PDF"
                    data-testid={`buyer-invoice-pdf-${inv.code}`}
                    className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-border hover:bg-secondary flex items-center gap-1">
                    <Download className="w-3 h-3"/>PDF
                  </button>
                  <button onClick={() => downloadFile(inv, "csv")} title="Download CSV"
                    data-testid={`buyer-invoice-csv-${inv.code}`}
                    className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-border hover:bg-secondary flex items-center gap-1">
                    <FileText className="w-3 h-3"/>CSV
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// BuyerAnalyticsPanel — per-supplier performance metrics for buyers.
// ─────────────────────────────────────────────────────────────────────
function BuyerAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dropship/buyer-analytics")
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="mt-10 text-center text-sm text-foreground/60 py-8">Crunching supplier stats…</div>;
  }
  if (!data || !data.per_supplier?.length) {
    return (
      <div className="mt-10 rounded-2xl border border-dashed border-border bg-secondary/20 px-6 py-12 text-center">
        <BarChart3 className="w-8 h-8 mx-auto text-foreground/30 mb-2"/>
        <div className="text-sm font-medium">No trading history yet</div>
        <div className="text-xs text-foreground/55 mt-1">Once you start placing orders with suppliers, per-supplier performance shows here.</div>
      </div>
    );
  }

  const { totals, per_supplier } = data;
  return (
    <div className="mt-10 space-y-6" data-testid="buyer-analytics-panel">
      <div>
        <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Supplier scorecard</div>
        <h3 className="display text-3xl mt-2 tracking-tight">Your supplier performance</h3>
        <p className="text-sm text-foreground/60 max-w-2xl mt-2">
          Cross-supplier fill rate, claim rate, and invoiced volume. Use it to renegotiate terms with underperformers and reward reliable partners.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Suppliers" value={totals.suppliers}/>
        <KpiTile label="Orders" value={totals.orders}/>
        <KpiTile label="Invoiced" value={money(totals.invoiced)} hint={`${money(totals.paid)} paid`}/>
        <KpiTile label="Avg fill rate" value={`${totals.fill_rate_avg}%`} hint={`${totals.claims} claim${totals.claims === 1 ? "" : "s"} filed`}/>
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="buyer-analytics-table">
        <div className="grid grid-cols-[1.5fr_90px_90px_110px_130px_120px_110px] gap-3 px-5 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
          <div>Supplier</div>
          <div className="text-right">Orders</div>
          <div className="text-right">Fill rate</div>
          <div className="text-right">Claim rate</div>
          <div className="text-right">Invoiced</div>
          <div className="text-right">Paid</div>
          <div className="text-right">Prior adj.</div>
        </div>
        <div className="divide-y divide-border">
          {per_supplier.map(row => (
            <div key={row.seller_id} className="grid grid-cols-[1.5fr_90px_90px_110px_130px_120px_110px] gap-3 px-5 py-3 items-center text-[13px]">
              <div className="min-w-0">
                <div className="font-medium truncate">{row.name}</div>
                <div className="text-[11px] text-foreground/55 truncate">{row.email || ""}</div>
              </div>
              <div className="text-right">{row.orders}</div>
              <div className={`text-right mono ${row.fill_rate >= 98 ? "text-emerald-700" : row.fill_rate >= 90 ? "text-foreground" : "text-rose-700"}`}>
                {row.fill_rate}%
              </div>
              <div className={`text-right mono ${row.claim_rate <= 2 ? "text-emerald-700" : row.claim_rate <= 5 ? "text-amber-700" : "text-rose-700"}`}>
                {row.claim_rate}%
              </div>
              <div className="text-right">{money(row.invoiced)}</div>
              <div className="text-right text-foreground/70">{money(row.paid)}</div>
              <div className="text-right text-indigo-700">{money(row.prior_adjustments_total)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

